import { maskitoUpdateElement, MASKITO_DEFAULT_OPTIONS, maskitoTransform } from '@maskito/core';

/**
 * Clamps a value between two inclusive limits
 */
function clamp(value, minimum, maximum) {
    const minClamped = max(minimum ?? value, value);
    return min(maximum ?? minClamped, minClamped);
}
function min(x, ...values) {
    return values.reduce((a, b) => (a < b ? a : b), x);
}
function max(x, ...values) {
    return values.reduce((a, b) => (a > b ? a : b), x);
}

function countDigits(str) {
    return str.replaceAll(/\W/g, '').length;
}

const DATE_SEGMENTS_MAX_VALUES = {
    day: 31,
    month: 12,
    year: 9999,
};

const DEFAULT_DECIMAL_PSEUDO_SEPARATORS = ['.', ',', 'б', 'ю'];

const DEFAULT_MIN_DATE = new Date('0001-01-01T00:00');
const DEFAULT_MAX_DATE = new Date('9999-12-31T23:59:59.999');

/**
 * {@link https://unicode-table.com/en/00A0/ Non-breaking space}.
 */
const CHAR_NO_BREAK_SPACE = '\u00A0';
/**
 * {@link https://symbl.cc/en/200B/ Zero width space}.
 */
const CHAR_ZERO_WIDTH_SPACE = '\u200B';
/**
 * {@link https://unicode-table.com/en/2013/ EN dash}
 * is used to indicate a range of numbers or a span of time.
 * @example 2006–2022
 */
const CHAR_EN_DASH = '\u2013';
/**
 * {@link https://unicode-table.com/en/2014/ EM dash}
 * is used to mark a break in a sentence.
 * @example Taiga UI — powerful set of open source components for Angular
 * ___
 * Don't confuse with {@link CHAR_EN_DASH} or {@link CHAR_HYPHEN}!
 */
const CHAR_EM_DASH = '\u2014';
/**
 * {@link https://unicode-table.com/en/002D/ Hyphen (minus sign)}
 * is used to combine words.
 * @example well-behaved
 * ___
 * Don't confuse with {@link CHAR_EN_DASH} or {@link CHAR_EM_DASH}!
 */
const CHAR_HYPHEN = '\u002D';
/**
 * {@link https://unicode-table.com/en/2212/ Minus}
 * is used as math operator symbol or before negative digits.
 * ---
 * Can be used as `&minus;`. Don't confuse with {@link CHAR_HYPHEN}
 */
const CHAR_MINUS = '\u2212';
/**
 * {@link https://symbl.cc/en/30FC/ Katakana-Hiragana Prolonged Sound Mark}
 * is used as prolonged sounds in Japanese.
 */
const CHAR_JP_HYPHEN = '\u30FC';
/**
 * {@link https://symbl.cc/en/003A/ Colon}
 * is a punctuation mark that connects parts of a text logically.
 * ---
 * is also used as separator in time.
 */
const CHAR_COLON = '\u003A';
/**
 * {@link https://symbl.cc/en/FF1A/ Full-width colon}
 * is a full-width punctuation mark used to separate parts of a text commonly in Japanese.
 */
const CHAR_JP_COLON = '\uFF1A';

const DEFAULT_PSEUDO_MINUSES = [
    CHAR_HYPHEN,
    CHAR_EN_DASH,
    CHAR_EM_DASH,
    CHAR_JP_HYPHEN,
    CHAR_MINUS,
];

const DEFAULT_TIME_SEGMENT_MAX_VALUES = {
    hours: 23,
    minutes: 59,
    seconds: 59,
    milliseconds: 999,
};
const DEFAULT_TIME_SEGMENT_MIN_VALUES = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
};

const TIME_FIXED_CHARACTERS = [':', '.'];

const TIME_SEGMENT_VALUE_LENGTHS = {
    hours: 2,
    minutes: 2,
    seconds: 2,
    milliseconds: 3,
};

const POSSIBLE_DATE_RANGE_SEPARATOR = [
    CHAR_HYPHEN,
    CHAR_EN_DASH,
    CHAR_EM_DASH,
    CHAR_MINUS,
    CHAR_JP_HYPHEN,
];
const MIN_DAY = 1;
const MONTHS_IN_YEAR = 12;
const MonthNumber = {
    January: 0,
    February: 1,
    April: 3,
    June: 5,
    September: 8,
    November: 10,
    December: 11,
};

function appendDate(date, { day = 0, month = 0, year = 0 } = {}) {
    if (day === 0 && month === 0 && year === 0) {
        return date;
    }
    const initialYear = date.getFullYear();
    const initialMonth = date.getMonth();
    const initialDate = date.getDate();
    const totalMonths = (initialYear + year) * MONTHS_IN_YEAR + initialMonth + month;
    let years = Math.floor(totalMonths / MONTHS_IN_YEAR);
    let months = totalMonths % MONTHS_IN_YEAR;
    const monthDaysCount = getMonthDaysCount(months, isLeapYear(years));
    const currentMonthDaysCount = getMonthDaysCount(initialMonth, isLeapYear(years));
    let days = day;
    if (initialDate >= monthDaysCount) {
        days += initialDate - (currentMonthDaysCount - monthDaysCount);
    }
    else if (currentMonthDaysCount < monthDaysCount &&
        initialDate === currentMonthDaysCount) {
        days += initialDate + (monthDaysCount - currentMonthDaysCount);
    }
    else {
        days += initialDate;
    }
    while (days > getMonthDaysCount(months, isLeapYear(years))) {
        days -= getMonthDaysCount(months, isLeapYear(years));
        if (months === MonthNumber.December) {
            years++;
            months = MonthNumber.January;
        }
        else {
            months++;
        }
    }
    while (days < MIN_DAY) {
        if (months === MonthNumber.January) {
            years--;
            months = MonthNumber.December;
        }
        else {
            months--;
        }
        days += getMonthDaysCount(months, isLeapYear(years));
    }
    days =
        day < 0 || month < 0 || year < 0
            ? days + 1 // add one day when moving days, or months, or years backward
            : days - 1; // "from"-day is included in the range
    return new Date(years, months, days);
}
function getMonthDaysCount(month, isLeapYear) {
    switch (month) {
        case MonthNumber.April:
        case MonthNumber.June:
        case MonthNumber.November:
        case MonthNumber.September:
            return 30;
        case MonthNumber.February:
            return isLeapYear ? 29 : 28;
        default:
            return 31;
    }
}
function isLeapYear(year) {
    return year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0);
}

const getDateSegmentValueLength = (dateString) => ({
    day: dateString.match(/d/g)?.length ?? 0,
    month: dateString.match(/m/g)?.length ?? 0,
    year: dateString.match(/y/g)?.length ?? 0,
});

function dateToSegments(date) {
    return {
        day: String(date.getDate()).padStart(2, '0'),
        month: String(date.getMonth() + 1).padStart(2, '0'),
        year: String(date.getFullYear()).padStart(4, '0'),
        hours: String(date.getHours()).padStart(2, '0'),
        minutes: String(date.getMinutes()).padStart(2, '0'),
        seconds: String(date.getSeconds()).padStart(2, '0'),
        milliseconds: String(date.getMilliseconds()).padStart(3, '0'),
    };
}

const ALL_POSSIBLE_SEGMENTS = [
    'day',
    'month',
    'year',
];
function getDateSegmentsOrder(template) {
    return [...ALL_POSSIBLE_SEGMENTS].sort((a, b) => template.indexOf(a[0]) > template.indexOf(b[0]) ? 1 : -1);
}

function getFirstCompleteDate(dateString, dateModeTemplate) {
    const digitsInDate = countDigits(dateModeTemplate);
    const [completeDate = ''] = new RegExp(String.raw `(\D*\d){${digitsInDate}}`).exec(dateString) || [];
    return completeDate;
}

function isDateStringComplete(dateString, dateModeTemplate) {
    return dateString.length < dateModeTemplate.length
        ? false
        : dateString.split(/\D/).every((segment) => !/^0+$/.exec(segment));
}

function parseDateRangeString(dateRange, dateModeTemplate, rangeSeparator) {
    const digitsInDate = countDigits(dateModeTemplate);
    return (dateRange
        .replace(rangeSeparator, '')
        .match(new RegExp(String.raw `(\D*\d[^\d\s]*){1,${digitsInDate}}`, 'g')) || []);
}

function parseDateString(dateString, fullMode) {
    const cleanMode = fullMode.replaceAll(/[^dmy]/g, '');
    const onlyDigitsDate = dateString.replaceAll(/\D+/g, '');
    const dateSegments = {
        day: onlyDigitsDate.slice(cleanMode.indexOf('d'), cleanMode.lastIndexOf('d') + 1),
        month: onlyDigitsDate.slice(cleanMode.indexOf('m'), cleanMode.lastIndexOf('m') + 1),
        year: onlyDigitsDate.slice(cleanMode.indexOf('y'), cleanMode.lastIndexOf('y') + 1),
    };
    return Object.fromEntries(Object.entries(dateSegments)
        .filter(([_, value]) => Boolean(value))
        .sort(([a], [b]) => fullMode.toLowerCase().indexOf(a.slice(0, 1)) >
        fullMode.toLowerCase().indexOf(b.slice(0, 1))
        ? 1
        : -1));
}

function segmentsToDate(parsedDate, parsedTime) {
    const year = parsedDate.year?.length === 2 ? `20${parsedDate.year}` : parsedDate.year;
    const date = new Date(Number(year ?? '0'), Number(parsedDate.month ?? '1') - 1, Number(parsedDate.day ?? '1'), Number(parsedTime?.hours ?? '0'), Number(parsedTime?.minutes ?? '0'), Number(parsedTime?.seconds ?? '0'), Number(parsedTime?.milliseconds ?? '0'));
    // needed for years less than 1900
    date.setFullYear(Number(year ?? '0'));
    return date;
}

const DATE_TIME_SEPARATOR = ', ';

function toDateString({ day, month, year, hours, minutes, seconds, milliseconds, }, { dateMode, dateTimeSeparator = DATE_TIME_SEPARATOR, timeMode, }) {
    const yearLength = dateMode.match(/y/g)?.length ?? 0;
    const fullMode = `${dateMode}${timeMode ? `${dateTimeSeparator}${timeMode}` : ''}`;
    return fullMode
        .replaceAll(/d+/g, day ?? '')
        .replaceAll(/m+/g, month ?? '')
        .replaceAll(/y+/g, year?.slice(-yearLength) ?? '')
        .replaceAll(/H+/g, hours ?? '')
        .replaceAll('MSS', milliseconds ?? '')
        .replaceAll(/M+/g, minutes ?? '')
        .replaceAll(/S+/g, seconds ?? '')
        .replaceAll(/^\D+/g, '')
        .replaceAll(/\D+$/g, '');
}

function validateDateString({ dateString, dateModeTemplate, dateSeparator, offset, selection: [from, to], }) {
    const parsedDate = parseDateString(dateString, dateModeTemplate);
    const dateSegments = Object.entries(parsedDate);
    const segmentsOrder = getDateSegmentsOrder(dateModeTemplate);
    const validatedDateSegments = {};
    for (let i = 0; i < dateSegments.length; i++) {
        const [segmentName, segmentValue] = dateSegments[i];
        const validatedDate = toDateString(validatedDateSegments, {
            dateMode: dateModeTemplate,
        });
        const maxSegmentValue = DATE_SEGMENTS_MAX_VALUES[segmentName];
        const fantomSeparator = validatedDate.length && dateSeparator.length;
        const lastSegmentDigitIndex = offset +
            validatedDate.length +
            fantomSeparator +
            getDateSegmentValueLength(dateModeTemplate)[segmentName];
        const isLastSegmentDigitAdded = lastSegmentDigitIndex >= from && lastSegmentDigitIndex === to;
        if (isLastSegmentDigitAdded && Number(segmentValue) > Number(maxSegmentValue)) {
            const nextSegment = segmentsOrder[segmentsOrder.indexOf(segmentName) + 1];
            if (!nextSegment || nextSegment === 'year') {
                // 31.1|0.2010 => Type 9 => 31.1|0.2010
                return { validatedDateString: '', updatedSelection: [from, to] }; // prevent changes
            }
            validatedDateSegments[segmentName] = `0${segmentValue.slice(0, -1)}`;
            dateSegments[i + 1] = [
                nextSegment,
                `${segmentValue.slice(-1)}${(dateSegments[i + 1]?.[1] ?? '').slice(1)}`,
            ];
            continue;
        }
        if (isLastSegmentDigitAdded && Number(segmentValue) < 1) {
            // 31.0|1.2010 => Type 0 => 31.0|1.2010
            return { validatedDateString: '', updatedSelection: [from, to] }; // prevent changes
        }
        validatedDateSegments[segmentName] = segmentValue;
    }
    const validatedDateString = toDateString(validatedDateSegments, {
        dateMode: dateModeTemplate,
    });
    const addedDateSeparators = validatedDateString.length - dateString.length;
    return {
        validatedDateString,
        updatedSelection: [from + addedDateSeparators, to + addedDateSeparators],
    };
}

function identity(x) {
    return x;
}
// eslint-disable-next-line  @typescript-eslint/no-empty-function
function noop() { }

/**
 * Copy-pasted solution from lodash
 * @see https://lodash.com/docs/4.17.15#escapeRegExp
 */
const reRegExpChar = /[\\^$.*+?()[\]{}|]/g;
const reHasRegExpChar = new RegExp(reRegExpChar.source);
function escapeRegExp(str) {
    return str && reHasRegExpChar.test(str)
        ? str.replaceAll(reRegExpChar, String.raw `\$&`)
        : str;
}

function findCommonBeginningSubstr(a, b) {
    let res = '';
    for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) {
            return res;
        }
        res += a[i];
    }
    return res;
}

function isEmpty(entity) {
    return !entity || (typeof entity === 'object' && Object.keys(entity).length === 0);
}

const ALL_ZEROES_RE = /^0+$/;
function padWithZeroesUntilValid(segmentValue, paddedMaxValue, prefixedZeroesCount = 0) {
    const paddedSegmentValue = segmentValue.padEnd(paddedMaxValue.length, '0');
    if (Number(paddedSegmentValue) <= Number(paddedMaxValue)) {
        return { validatedSegmentValue: segmentValue, prefixedZeroesCount };
    }
    if (paddedSegmentValue.endsWith('0')) {
        // 00:|00 => Type 9 => 00:09|
        return padWithZeroesUntilValid(`0${segmentValue.slice(0, paddedMaxValue.length - 1)}`, paddedMaxValue, prefixedZeroesCount + 1);
    }
    const valueWithoutLastChar = segmentValue.slice(0, paddedMaxValue.length - 1);
    if (ALL_ZEROES_RE.exec(valueWithoutLastChar)) {
        return { validatedSegmentValue: '', prefixedZeroesCount };
    }
    // |19:00 => Type 2 => 2|0:00
    return padWithZeroesUntilValid(`${valueWithoutLastChar}0`, paddedMaxValue, prefixedZeroesCount);
}

/**
 * Replace fullwidth colon with half width colon
 * @param fullWidthColon full width colon
 * @returns processed half width colon
 */
function toHalfWidthColon(fullWidthColon) {
    return fullWidthColon.replaceAll(new RegExp(CHAR_JP_COLON, 'g'), CHAR_COLON);
}

/**
 * Replace fullwidth numbers with half width number
 * @param fullWidthNumber full width number
 * @returns processed half width number
 */
function toHalfWidthNumber(fullWidthNumber) {
    return fullWidthNumber.replaceAll(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0));
}

/**
 * Convert full width colon (：) to half width one (:)
 */
function createColonConvertPreprocessor() {
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        return {
            elementState: {
                selection,
                value: toHalfWidthColon(value),
            },
            data: toHalfWidthColon(data),
        };
    };
}

function createDateSegmentsZeroPaddingPostprocessor({ dateModeTemplate, dateSeparator, splitFn, uniteFn, }) {
    return ({ value, selection }) => {
        const [from, to] = selection;
        const { dateStrings, restPart = '' } = splitFn(value);
        const validatedDateStrings = [];
        let caretShift = 0;
        dateStrings.forEach((dateString) => {
            const parsedDate = parseDateString(dateString, dateModeTemplate);
            const dateSegments = Object.entries(parsedDate);
            const validatedDateSegments = dateSegments.reduce((acc, [segmentName, segmentValue]) => {
                const { validatedSegmentValue, prefixedZeroesCount } = padWithZeroesUntilValid(segmentValue, `${DATE_SEGMENTS_MAX_VALUES[segmentName]}`);
                caretShift += prefixedZeroesCount;
                return { ...acc, [segmentName]: validatedSegmentValue };
            }, {});
            validatedDateStrings.push(toDateString(validatedDateSegments, { dateMode: dateModeTemplate }));
        });
        const validatedValue = `${uniteFn(validatedDateStrings, value)}${dateStrings[dateStrings.length - 1]?.endsWith(dateSeparator)
            ? dateSeparator
            : ''}${restPart}`;
        if (caretShift &&
            validatedValue.slice(to + caretShift, to + caretShift + dateSeparator.length) === dateSeparator) {
            /**
             * If `caretShift` > 0, it means that time segment was padded with zero.
             * It is only possible if any character insertion happens.
             * If caret is before `dateSeparator` => it should be moved after `dateSeparator`.
             */
            caretShift += dateSeparator.length;
        }
        return {
            selection: [from + caretShift, to + caretShift],
            value: validatedValue,
        };
    };
}

/**
 * It replaces pseudo range separators with valid one.
 * @example '01.01.2000_11.11.2000' -> '01.01.2000 - 01.01.2000'
 * @example '01.01.2000_23:59' -> '01.01.2000, 23:59'
 */
function createFirstDateEndSeparatorPreprocessor({ dateModeTemplate, firstDateEndSeparator, dateSeparator, pseudoFirstDateEndSeparators, }) {
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        const [from, to] = selection;
        const firstCompleteDate = getFirstCompleteDate(value, dateModeTemplate);
        const pseudoSeparators = pseudoFirstDateEndSeparators.filter((x) => !firstDateEndSeparator.includes(x) && x !== dateSeparator);
        const pseudoSeparatorsRE = new RegExp(`[${pseudoSeparators.join('')}]`, 'gi');
        const newValue = firstCompleteDate && value.length > firstCompleteDate.length
            ? `${firstCompleteDate}${value
                .slice(firstCompleteDate.length)
                .replace(/^\D*/, firstDateEndSeparator)}`
            : value;
        const caretShift = newValue.length - value.length;
        return {
            elementState: {
                selection: [from + caretShift, to + caretShift],
                value: newValue,
            },
            data: data.replace(pseudoSeparatorsRE, firstDateEndSeparator),
        };
    };
}

/**
 * Convert full width numbers like １, ２ to half width numbers 1, 2
 */
function createFullWidthToHalfWidthPreprocessor() {
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        return {
            elementState: {
                selection,
                value: toHalfWidthNumber(value),
            },
            data: toHalfWidthNumber(data),
        };
    };
}

function hasDayPeriod(dayPeriod) {
    return dayPeriod.every(Boolean);
}
function createDayPeriodMatchers(dayPeriod) {
    const [am, pm] = dayPeriod;
    const uniqueChars = [...new Set([...am, ...pm])].map(escapeRegExp).join('');
    return {
        anyDayPeriodCharRE: new RegExp(`[${CHAR_NO_BREAK_SPACE}${uniqueChars}]+$`, 'gi'),
        fullDayPeriodRE: new RegExp(`${CHAR_NO_BREAK_SPACE}(?:${escapeRegExp(am)}|${escapeRegExp(pm)})$`, 'gi'),
        initialCharRE: new RegExp(`^[${escapeRegExp(am[0])}${escapeRegExp(pm[0])}]$`, 'i'),
    };
}

function createTimeMaskExpression({ mode, separators, dayPeriod, }) {
    let separatorIndex = 0;
    return Array.from(mode.replace(' AA', ''))
        .flatMap((char) => TIME_FIXED_CHARACTERS.includes(char)
        ? Array.from(separators[separatorIndex++])
        : [/\d/])
        .concat(hasDayPeriod(dayPeriod)
        ? [
            CHAR_NO_BREAK_SPACE,
            ...dayPeriod[0]
                .split('')
                .map(escapeRegExp)
                .map((am, i) => new RegExp(`[${am}${escapeRegExp(dayPeriod[1][i])}]`, 'i')),
        ]
        : []);
}

function padTimeSegments(timeSegments, pad) {
    return Object.fromEntries(Object.entries(timeSegments).map(([segmentName, segmentValue]) => [
        segmentName,
        pad(String(segmentValue), TIME_SEGMENT_VALUE_LENGTHS[segmentName]),
    ]));
}

function padStartTimeSegments(timeSegments) {
    return padTimeSegments(timeSegments, (value, length) => value.padStart(length, '0'));
}

const SEGMENT_FULL_NAME = {
    HH: 'hours',
    MM: 'minutes',
    SS: 'seconds',
    MSS: 'milliseconds',
};
/**
 * @param timeString can be with/without fixed characters
 */
function parseTimeString(timeString, timeMode) {
    const onlyDigits = timeString.replaceAll(/\D+/g, '');
    let offset = 0;
    return Object.fromEntries(timeMode
        .split(/\W/)
        .filter((segmentAbbr) => SEGMENT_FULL_NAME[segmentAbbr])
        .map((segmentAbbr) => {
        const segmentValue = onlyDigits.slice(offset, offset + segmentAbbr.length);
        offset += segmentAbbr.length;
        return [SEGMENT_FULL_NAME[segmentAbbr], segmentValue];
    }));
}

function toTimeString(segments, { mode, separators }) {
    let separatorIndex = 0;
    const modeTemplate = mode
        .replace(' AA', '')
        .replaceAll(/[:.]/g, () => separators[separatorIndex++]);
    return modeTemplate
        .replaceAll(/H+/g, segments.hours ?? '')
        .replaceAll('MSS', segments.milliseconds ?? '')
        .replaceAll(/M+/g, segments.minutes ?? '')
        .replaceAll(/S+/g, segments.seconds ?? '')
        .replace(/^\D*/, '')
        .replace(/\D*$/, '');
}

/**
 * Pads invalid time segment with zero to make it valid.
 * @example 00:|00 => Type 9 (too much for the first digit of minutes) => 00:09|
 * @example |19:00 => Type 2 (29 - invalid value for hours) => 2|0:00
 */
function enrichTimeSegmentsWithZeroes({ value, selection }, { mode, separators, timeSegmentMaxValues = DEFAULT_TIME_SEGMENT_MAX_VALUES, }) {
    const [from, to] = selection;
    const parsedTime = parseTimeString(value, mode);
    const possibleTimeSegments = Object.entries(parsedTime);
    const paddedMaxValues = padStartTimeSegments(timeSegmentMaxValues);
    const validatedTimeSegments = {};
    let paddedZeroes = 0;
    for (const [segmentName, segmentValue] of possibleTimeSegments) {
        const maxSegmentValue = paddedMaxValues[segmentName];
        const { validatedSegmentValue, prefixedZeroesCount } = padWithZeroesUntilValid(segmentValue, String(maxSegmentValue));
        paddedZeroes += prefixedZeroesCount;
        validatedTimeSegments[segmentName] = validatedSegmentValue;
    }
    const [leadingNonDigitCharacters = ''] = value.match(/^\D+(?=\d)/g) || []; // prefix
    const [trailingNonDigitCharacters = ''] = value.match(/\D+$/g) || []; // trailing segment separators / meridiem characters / postfix
    const time = toTimeString(validatedTimeSegments, { mode, separators });
    const validatedTime = `${leadingNonDigitCharacters}${time}${trailingNonDigitCharacters}`;
    const innerPart = validatedTime.slice(leadingNonDigitCharacters.length, validatedTime.length - trailingNonDigitCharacters.length);
    const uniqueSeparatorChars = [
        ...new Set(Array.from(innerPart).filter((char) => !/\d/.test(char))),
    ].map(escapeRegExp);
    const addedTimeSeparators = Math.max(validatedTime.length - value.length - paddedZeroes, 0);
    let newFrom = from + paddedZeroes + addedTimeSeparators;
    let newTo = to + paddedZeroes + addedTimeSeparators;
    const caretWasShiftedByZeroPadding = newFrom === newTo && paddedZeroes > 0 && uniqueSeparatorChars.length > 0;
    if (caretWasShiftedByZeroPadding) {
        const trailingTimeSegmentSeparatorReg = new RegExp(`[${uniqueSeparatorChars.join('')}]$`);
        const cursorIsRightBeforeSeparator = validatedTime
            .slice(0, newTo + 1)
            .match(trailingTimeSegmentSeparatorReg);
        if (cursorIsRightBeforeSeparator) {
            newFrom++;
            newTo++;
        }
    }
    return {
        value: validatedTime,
        selection: [newFrom, newTo],
    };
}

/**
 * Prevent insertion if any time segment will become invalid
 * (and even zero padding won't help with it).
 * @example 2|0:00 => Type 9 => 2|0:00
 */
function createInvalidTimeSegmentInsertionPreprocessor({ mode, timeSegmentMinValues, timeSegmentMaxValues, parseValue = (x) => ({ timeString: x }), }) {
    const invalidCharsRegExp = new RegExp(String.raw `[^\d${TIME_FIXED_CHARACTERS.map(escapeRegExp).join('')}]+`);
    return ({ elementState, data }, actionType) => {
        if (actionType !== 'insert') {
            return { elementState, data };
        }
        const { value, selection } = elementState;
        const [from, rawTo] = selection;
        const newCharacters = data.replace(invalidCharsRegExp, '');
        const to = rawTo + newCharacters.length; // to be conformed with `overwriteMode: replace`
        const newPossibleValue = `${value.slice(0, from)}${newCharacters}${value.slice(to)}`;
        const { timeString, restValue = '' } = parseValue(newPossibleValue);
        const timeSegments = Object.entries(parseTimeString(timeString, mode));
        let offset = restValue.length;
        for (const [segmentName, stringifiedSegmentValue] of timeSegments) {
            const minSegmentValue = timeSegmentMinValues[segmentName];
            const maxSegmentValue = timeSegmentMaxValues[segmentName];
            const segmentValue = Number(stringifiedSegmentValue);
            const lastSegmentDigitIndex = offset + TIME_SEGMENT_VALUE_LENGTHS[segmentName];
            if (lastSegmentDigitIndex >= from &&
                lastSegmentDigitIndex <= to &&
                segmentValue !== clamp(segmentValue, minSegmentValue, maxSegmentValue)) {
                return { elementState, data: '' }; // prevent insertion
            }
            offset +=
                stringifiedSegmentValue.length +
                    // any time segment separator
                    1;
        }
        return { elementState, data };
    };
}

function createMeridiemPreprocessor(dayPeriod) {
    if (!hasDayPeriod(dayPeriod)) {
        return identity;
    }
    const { fullDayPeriodRE, initialCharRE } = createDayPeriodMatchers(dayPeriod);
    const [am, pm] = dayPeriod;
    return ({ elementState, data }) => {
        const { selection, value } = elementState;
        return value.match(fullDayPeriodRE) && data.match(initialCharRE)
            ? {
                elementState: {
                    value: value.replaceAll(fullDayPeriodRE, ''),
                    selection,
                },
                data: pickMeridiem(data, am, pm),
            }
            : { elementState, data };
    };
}
function createMeridiemPostprocessor(dayPeriod) {
    if (!hasDayPeriod(dayPeriod)) {
        return identity;
    }
    const { anyDayPeriodCharRE, fullDayPeriodRE } = createDayPeriodMatchers(dayPeriod);
    const [am, pm] = dayPeriod;
    return ({ value, selection }, initialElementState) => {
        if (!value.match(anyDayPeriodCharRE) ||
            dayPeriod.some((x) => value.includes(x))) {
            return { value, selection };
        }
        const [from, to] = selection;
        // any meridiem character was deleted
        if (initialElementState.value.match(fullDayPeriodRE)) {
            const newValue = value.replace(anyDayPeriodCharRE, '');
            return {
                value: newValue,
                selection: [
                    Math.min(from, newValue.length),
                    Math.min(to, newValue.length),
                ],
            };
        }
        const fullMeridiem = `${CHAR_NO_BREAK_SPACE}${pickMeridiem(value, am, pm)}`;
        const newValue = value.replace(anyDayPeriodCharRE, (x) => x === CHAR_NO_BREAK_SPACE ? x : fullMeridiem);
        return {
            value: newValue,
            selection: to >= newValue.indexOf(fullMeridiem)
                ? [newValue.length, newValue.length]
                : selection,
        };
    };
}
function pickMeridiem(value, am, pm) {
    const valueLc = value.toLowerCase();
    for (let i = 0; i < Math.min(am.length, pm.length); i++) {
        const amCharLc = am[i].toLowerCase();
        const pmCharLc = pm[i].toLowerCase();
        if (amCharLc === pmCharLc) {
            continue;
        }
        return valueLc.includes(pmCharLc) ? pm : am;
    }
    return am;
}

function raiseSegmentValueToMin(segments, fullMode) {
    const segmentsLength = getDateSegmentValueLength(fullMode);
    return Object.fromEntries(Object.entries(segments).map(([key, value]) => {
        const segmentLength = segmentsLength[key];
        return [
            key,
            value.length === segmentLength && /^0+$/.exec(value)
                ? '1'.padStart(segmentLength, '0')
                : value,
        ];
    }));
}

const LEAP_YEAR = '1972';
function createMinMaxDatePostprocessor({ dateModeTemplate, min, max, dateSeparator, rangeSeparator = '', }) {
    return ({ value, selection }) => {
        const endsWithRangeSeparator = rangeSeparator && value.endsWith(rangeSeparator);
        const dateStrings = parseDateRangeString(value, dateModeTemplate, rangeSeparator);
        let validatedValue = '';
        for (const dateString of dateStrings) {
            validatedValue += validatedValue ? rangeSeparator : '';
            const parsedDate = parseDateString(dateString, dateModeTemplate);
            if (!isDateStringComplete(dateString, dateModeTemplate)) {
                const fixedDate = raiseSegmentValueToMin(parsedDate, dateModeTemplate);
                const fixedValue = toDateString(fixedDate, { dateMode: dateModeTemplate });
                const tail = dateString.endsWith(dateSeparator) ? dateSeparator : '';
                validatedValue += `${fixedValue}${tail}`;
                continue;
            }
            const date = segmentsToDate({ year: LEAP_YEAR, ...parsedDate });
            const clampedDate = clamp(date, min, max);
            validatedValue += toDateString(dateToSegments(clampedDate), {
                dateMode: dateModeTemplate,
            });
        }
        return {
            selection,
            value: `${validatedValue}${endsWithRangeSeparator ? rangeSeparator : ''}`,
        };
    };
}

function normalizeDatePreprocessor({ dateModeTemplate, dateSeparator, rangeSeparator = '', dateTimeSeparator = DATE_TIME_SEPARATOR, }) {
    return ({ elementState, data }) => {
        const templateSegments = dateModeTemplate.split(dateSeparator);
        const includesTime = data.includes(dateTimeSeparator);
        const dateSegments = data
            .slice(0, includesTime ? data.indexOf(dateTimeSeparator) : Infinity)
            .split(/\D/)
            .filter(Boolean);
        if (!dateSegments.length || dateSegments.length % templateSegments.length !== 0) {
            return { elementState, data };
        }
        const dates = dateSegments.reduce((dates, segment, index) => {
            const template = templateSegments[index % templateSegments.length] ?? '';
            const dateIndex = Math.trunc(index / templateSegments.length);
            const isLastDateSegment = index % templateSegments.length === templateSegments.length - 1;
            if (!dates[dateIndex]) {
                dates[dateIndex] = '';
            }
            dates[dateIndex] += isLastDateSegment
                ? segment
                : `${segment.padStart(template.length, '0')}${dateSeparator}`;
            return dates;
        }, []);
        return {
            elementState,
            data: includesTime
                ? `${dates[0]}${data.slice(data.indexOf(dateTimeSeparator))}`
                : dates.join(rangeSeparator),
        };
    };
}

function maskitoPostfixPostprocessorGenerator(postfix) {
    const completedPostfixRE = new RegExp(`${escapeRegExp(postfix)}$`);
    const incompletePostfixRE = new RegExp(postfix &&
        `(${postfix
            .split('')
            .map(escapeRegExp)
            // eslint-disable-next-line
            .reduce((acc, _, i, arr) => `${acc}|${arr.slice(0, i + 1).join('')}`)})$`);
    return postfix
        ? ({ value, selection }, initialElementState) => {
            if (!value && !initialElementState.value.endsWith(postfix)) {
                // cases when developer wants input to be empty (programmatically)
                return { value, selection };
            }
            if (!value.match(incompletePostfixRE) &&
                !initialElementState.value.endsWith(postfix)) {
                return { selection, value: `${value}${postfix}` };
            }
            const initialValueBeforePostfix = initialElementState.value.replace(completedPostfixRE, '');
            const postfixWasModified = initialElementState.selection[1] > initialValueBeforePostfix.length;
            const alreadyExistedValueBeforePostfix = findCommonBeginningSubstr(initialValueBeforePostfix, value);
            return {
                selection,
                value: Array.from(postfix)
                    .reverse()
                    .reduce((newValue, char, index) => {
                    const i = newValue.length - 1 - index;
                    const isInitiallyMirroredChar = alreadyExistedValueBeforePostfix[i] === char &&
                        postfixWasModified;
                    return newValue[i] !== char || isInitiallyMirroredChar
                        ? `${newValue.slice(0, i + 1)}${char}${newValue.slice(i + 1)}`
                        : newValue;
                }, value),
            };
        }
        : identity;
}

function maskitoPrefixPostprocessorGenerator(prefix) {
    return prefix
        ? ({ value, selection }, initialElementState) => {
            if (value.startsWith(prefix) || // already valid
                (!value && !initialElementState.value.startsWith(prefix)) // cases when developer wants input to be empty
            ) {
                return { value, selection };
            }
            const [from, to] = selection;
            const prefixedValue = Array.from(prefix).reduce((modifiedValue, char, i) => modifiedValue[i] === char
                ? modifiedValue
                : `${modifiedValue.slice(0, i)}${char}${modifiedValue.slice(i)}`, value);
            const addedCharsCount = prefixedValue.length - value.length;
            return {
                selection: [from + addedCharsCount, to + addedCharsCount],
                value: prefixedValue,
            };
        }
        : identity;
}

const DIGIT_REGEXP = /\d/;
const NON_DIGIT_REGEXP = /\D/;
function skipNonDigitCharacters(value, [from, to]) {
    if (from !== to || from <= 0 || !DIGIT_REGEXP.test(value.charAt(from - 1))) {
        return [from, to];
    }
    let shifted = from;
    while (shifted < value.length && NON_DIGIT_REGEXP.test(value.charAt(shifted))) {
        shifted++;
    }
    return [shifted, shifted];
}

function createValidDatePreprocessor({ dateModeTemplate, dateSeparator, rangeSeparator = '', }) {
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        if (data === dateSeparator) {
            return {
                elementState,
                data: selection[0] === value.length ? data : '',
            };
        }
        if (!data.replaceAll(/\D/g, '')) {
            return { elementState, data };
        }
        const newCharacters = data.replaceAll(new RegExp(String.raw `[^\d${escapeRegExp(dateSeparator)}${rangeSeparator}]`, 'g'), '');
        const [from, rawTo] = skipNonDigitCharacters(value, selection);
        let to = rawTo + data.length;
        const newPossibleValue = `${value.slice(0, from)}${newCharacters}${value.slice(to)}`;
        const dateStrings = parseDateRangeString(newPossibleValue, dateModeTemplate, rangeSeparator);
        let validatedValue = '';
        const hasRangeSeparator = Boolean(rangeSeparator) && newPossibleValue.includes(rangeSeparator);
        for (const dateString of dateStrings) {
            const { validatedDateString, updatedSelection } = validateDateString({
                dateString,
                dateModeTemplate,
                dateSeparator,
                offset: validatedValue.length,
                selection: [from, to],
            });
            if (dateString && !validatedDateString) {
                return { elementState, data: '' }; // prevent changes
            }
            to = updatedSelection[1];
            validatedValue +=
                hasRangeSeparator && !validatedValue
                    ? `${validatedDateString}${rangeSeparator}`
                    : validatedDateString;
        }
        const newData = validatedValue.slice(from, to);
        return {
            elementState: {
                selection: [from, rawTo],
                value: `${validatedValue.slice(0, from)}${newData
                    .split(dateSeparator)
                    .map((segment) => '0'.repeat(segment.length))
                    .join(dateSeparator)}${validatedValue.slice(to)}`,
            },
            data: newData,
        };
    };
}

function maskitoEventHandler(name, handler, eventListenerOptions) {
    return (element, maskitoOptions) => {
        const listener = () => handler(element, maskitoOptions);
        element.addEventListener(name, listener, eventListenerOptions);
        return () => element.removeEventListener(name, listener, eventListenerOptions);
    };
}

function maskitoAddOnFocusPlugin(value) {
    return maskitoEventHandler('focus', (element) => {
        if (!element.value) {
            maskitoUpdateElement(element, value);
        }
    });
}

function maskitoSelectionChangeHandler(handler) {
    return (element, options) => {
        const document = element.ownerDocument;
        let isPointerDown = 0;
        const onPointerDown = () => isPointerDown++;
        const onPointerUp = () => {
            isPointerDown = Math.max(--isPointerDown, 0);
        };
        const listener = () => {
            if (!element.matches(':focus')) {
                return;
            }
            if (isPointerDown) {
                return document.addEventListener('mouseup', listener, {
                    once: true,
                    passive: true,
                });
            }
            handler(element, options);
        };
        document.addEventListener('selectionchange', listener, { passive: true });
        // Safari does not fire `selectionchange` on focus after programmatic update of textfield value
        element.addEventListener('focus', listener, { passive: true });
        element.addEventListener('mousedown', onPointerDown, { passive: true });
        document.addEventListener('mouseup', onPointerUp, { passive: true });
        return () => {
            document.removeEventListener('selectionchange', listener);
            element.removeEventListener('focus', listener);
            element.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('mouseup', onPointerUp);
        };
    };
}

function maskitoCaretGuard(guard) {
    return maskitoSelectionChangeHandler((element) => {
        const start = element.selectionStart ?? 0;
        const end = element.selectionEnd ?? 0;
        const [fromLimit, toLimit] = guard(element.value, [start, end]);
        if (fromLimit > start || toLimit < end) {
            element.setSelectionRange(clamp(start, fromLimit, toLimit), clamp(end, fromLimit, toLimit));
        }
    });
}

const maskitoRejectEvent = (element) => {
    const listener = () => {
        const value = element.value;
        element.addEventListener('beforeinput', (event) => {
            if (event.defaultPrevented && value === element.value) {
                element.dispatchEvent(new CustomEvent('maskitoReject', { bubbles: true }));
            }
        }, { once: true });
    };
    element.addEventListener('beforeinput', listener, true);
    return () => element.removeEventListener('beforeinput', listener, true);
};

function maskitoRemoveOnBlurPlugin(value) {
    return maskitoEventHandler('blur', (element) => {
        if (element.value === value) {
            maskitoUpdateElement(element, '');
        }
    });
}

function createMeridiemSteppingPlugin({ dayPeriod, meridiemStartIndex, }) {
    if (meridiemStartIndex < 0 || !hasDayPeriod(dayPeriod)) {
        return noop;
    }
    const { anyDayPeriodCharRE } = createDayPeriodMatchers(dayPeriod);
    const [am, pm] = dayPeriod;
    return (element) => {
        const listener = (event) => {
            const caretIndex = Number(element.selectionStart);
            const { value } = element;
            if ((event.key !== 'ArrowUp' && event.key !== 'ArrowDown') ||
                caretIndex < meridiemStartIndex) {
                return;
            }
            event.preventDefault();
            // eslint-disable-next-line no-nested-ternary
            const nextMeridiem = value.toLowerCase().includes(am.toLowerCase())
                ? pm
                : value.toLowerCase().includes(pm.toLowerCase()) ||
                    event.key === 'ArrowUp'
                    ? am
                    : pm;
            const newMeridiem = `${CHAR_NO_BREAK_SPACE}${nextMeridiem}`;
            maskitoUpdateElement(element, {
                value: value.length === meridiemStartIndex
                    ? `${value}${newMeridiem}`
                    : value.replace(anyDayPeriodCharRE, newMeridiem),
                selection: [caretIndex, caretIndex],
            });
        };
        element.addEventListener('keydown', listener);
        return () => element.removeEventListener('keydown', listener);
    };
}

function createTimeSegmentsSteppingPlugin({ step, fullMode, timeSegmentMinValues, timeSegmentMaxValues, dayPeriod, }) {
    const segmentsIndexes = createTimeSegmentsIndexes(fullMode);
    return step <= 0
        ? noop
        : (element) => {
            const listener = (event) => {
                if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
                    return;
                }
                event.preventDefault();
                const selectionStart = element.selectionStart ?? 0;
                const activeSegment = getActiveSegment({
                    segmentsIndexes,
                    selectionStart,
                });
                if (!activeSegment) {
                    return;
                }
                const toAdd = event.key === 'ArrowUp' ? step : -step;
                const selection = segmentsIndexes.get(activeSegment);
                const meridiem = hasDayPeriod(dayPeriod) ? dayPeriod : null;
                const previousValue = element.value;
                const updatedValue = updateSegmentValue({
                    selection,
                    value: previousValue,
                    toAdd,
                    min: timeSegmentMinValues[activeSegment],
                    max: timeSegmentMaxValues[activeSegment],
                });
                maskitoUpdateElement(element, {
                    value: meridiem &&
                        shouldToggleMeridiem({
                            activeSegment,
                            previousValue,
                            selection,
                            toAdd,
                        })
                        ? toggleMeridiem(updatedValue, meridiem)
                        : updatedValue,
                    selection: [selectionStart, selectionStart],
                });
            };
            element.addEventListener('keydown', listener);
            return () => element.removeEventListener('keydown', listener);
        };
}
function createTimeSegmentsIndexes(fullMode) {
    return new Map([
        ['hours', getSegmentRange(fullMode, 'HH')],
        ['milliseconds', getSegmentRange(fullMode, 'MSS')],
        ['minutes', getSegmentRange(fullMode, 'MM')],
        ['seconds', getSegmentRange(fullMode, 'SS')],
    ]);
}
function getSegmentRange(mode, segment) {
    const index = mode.indexOf(segment);
    return index === -1 ? [-1, -1] : [index, index + segment.length];
}
function getActiveSegment({ segmentsIndexes, selectionStart, }) {
    for (const [segmentName, segmentRange] of segmentsIndexes.entries()) {
        const [from, to] = segmentRange;
        if (from <= selectionStart && selectionStart <= to) {
            return segmentName;
        }
    }
    return null;
}
function updateSegmentValue({ selection, value, toAdd, min, max, }) {
    const [from, to] = selection;
    const segmentValue = Number(value.slice(from, to).padEnd(to - from, '0'));
    const newSegmentValue = mod(segmentValue + toAdd, min, max + 1);
    return `${value.slice(0, from)}${String(newSegmentValue).padStart(to - from, '0')}${value.slice(to)}`;
}
function mod(value, min, max) {
    const range = max - min;
    return ((((value - min) % range) + range) % range) + min;
}
function shouldToggleMeridiem({ activeSegment, previousValue, selection, toAdd, }) {
    if (activeSegment !== 'hours') {
        return false;
    }
    const [segmentStart, segmentEnd] = selection;
    const segmentLength = segmentEnd - segmentStart;
    const previousHour = Number(previousValue.slice(segmentStart, segmentEnd).padEnd(segmentLength, '0'));
    const dialIndex = previousHour % 12;
    const wraps = Math.floor((dialIndex + toAdd) / 12);
    return Math.abs(wraps) % 2 === 1;
}
function toggleMeridiem(value, dayPeriod) {
    const [am, pm] = dayPeriod;
    const lowerValue = value.toLowerCase();
    const containsAm = lowerValue.includes(am.toLowerCase());
    const containsPm = lowerValue.includes(pm.toLowerCase());
    if (!containsAm && !containsPm) {
        return value;
    }
    const nextMeridiem = containsAm ? pm : am;
    const { anyDayPeriodCharRE } = createDayPeriodMatchers(dayPeriod);
    return value.replace(anyDayPeriodCharRE, `${CHAR_NO_BREAK_SPACE}${nextMeridiem}`);
}

function maskitoWithPlaceholder(placeholder, focusedOnly = false) {
    let lastClearValue = '';
    let action = 'validation';
    const removePlaceholder = (value) => {
        for (let i = value.length - 1; i >= lastClearValue.length; i--) {
            if (value[i] !== placeholder[i]) {
                return value.slice(0, i + 1);
            }
        }
        return value.slice(0, lastClearValue.length);
    };
    const plugins = [maskitoCaretGuard((value) => [0, removePlaceholder(value).length])];
    let focused = false;
    if (focusedOnly) {
        const focus = maskitoEventHandler('focus', (element) => {
            focused = true;
            maskitoUpdateElement(element, `${element.value}${placeholder.slice(element.value.length)}`);
        }, { capture: true });
        const blur = maskitoEventHandler('blur', (element) => {
            focused = false;
            maskitoUpdateElement(element, removePlaceholder(element.value));
        }, { capture: true });
        plugins.push(focus, blur);
    }
    return {
        plugins,
        removePlaceholder,
        preprocessors: [
            ({ elementState, data }, actionType) => {
                action = actionType;
                const { value, selection } = elementState;
                return {
                    elementState: {
                        selection,
                        value: removePlaceholder(value),
                    },
                    data,
                };
            },
        ],
        postprocessors: [
            ({ value, selection }, initialElementState) => {
                lastClearValue = value;
                const justPlaceholderRemoval = `${value}${placeholder.slice(value.length, initialElementState.value.length)}` === initialElementState.value;
                if (action === 'validation' && justPlaceholderRemoval) {
                    /**
                     * If `value` still equals to `initialElementState.value`,
                     * then it means that value is patched programmatically (from Maskito's plugin or externally).
                     * In this case, we don't want to mutate value and automatically add/remove placeholder.
                     * ___
                     * For example, developer wants to remove manually placeholder (+ do something else with value) on blur.
                     * Without this condition, placeholder will be unexpectedly added again.
                     */
                    return { selection, value: initialElementState.value };
                }
                const newValue = focused || !focusedOnly
                    ? `${value}${placeholder.slice(value.length)}`
                    : value;
                if (newValue === initialElementState.value &&
                    action === 'deleteBackward') {
                    const [caretIndex] = initialElementState.selection;
                    return {
                        value: newValue,
                        selection: [caretIndex, caretIndex],
                    };
                }
                return { value: newValue, selection };
            },
        ],
    };
}

function createZeroPlaceholdersPreprocessor(postfix = '') {
    const isLastChar = (value, [_, to]) => to >= value.length - postfix.length;
    return ({ elementState }, actionType) => {
        const { value, selection } = elementState;
        if (!value || isLastChar(value, selection)) {
            return { elementState };
        }
        const [from, to] = selection;
        const zeroes = value.slice(from, to).replaceAll(/\d/g, '0');
        const newValue = `${value.slice(0, from)}${zeroes}${value.slice(to)}`;
        if (!zeroes.replaceAll(/\D/g, '')) {
            return { elementState };
        }
        return actionType === 'validation' || (actionType === 'insert' && from === to)
            ? { elementState: { selection, value: newValue } }
            : {
                elementState: {
                    selection: actionType === 'deleteBackward' || actionType === 'insert'
                        ? [from, from]
                        : [to, to],
                    value: newValue,
                },
            };
    };
}

function getLocaleDateParams(locale) {
    const referenceDate = new Date(1970, 0, 2);
    const parts = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(referenceDate);
    const separator = parts.find((part) => part.type === 'literal')?.value ?? '.';
    const segmentLabel = { day: 'dd', month: 'mm', year: 'yyyy' };
    const mode = parts
        .filter((part) => part.type === 'year' || part.type === 'month' || part.type === 'day')
        .map((part) => segmentLabel[part.type])
        .join('/');
    return { mode, separator };
}

function withDateDefaults({ locale = '', mode, separator, min = DEFAULT_MIN_DATE, max = DEFAULT_MAX_DATE, }) {
    const localeParams = locale ? getLocaleDateParams(locale) : null;
    return {
        locale,
        mode: mode ?? localeParams?.mode ?? 'dd/mm/yyyy',
        separator: separator ?? localeParams?.separator ?? '.',
        min,
        max,
    };
}

function maskitoDate(params) {
    const { mode, min, max, separator: dateSeparator } = withDateDefaults(params);
    const dateModeTemplate = mode.split('/').join(dateSeparator);
    return {
        ...MASKITO_DEFAULT_OPTIONS,
        mask: [...dateModeTemplate].map((char) => dateSeparator.includes(char) ? char : /\d/),
        overwriteMode: 'replace',
        preprocessors: [
            createFullWidthToHalfWidthPreprocessor(),
            createZeroPlaceholdersPreprocessor(),
            normalizeDatePreprocessor({
                dateModeTemplate,
                dateSeparator,
            }),
            createValidDatePreprocessor({
                dateModeTemplate,
                dateSeparator,
            }),
        ],
        postprocessors: [
            createDateSegmentsZeroPaddingPostprocessor({
                dateModeTemplate,
                dateSeparator,
                splitFn: (value) => ({ dateStrings: [value] }),
                uniteFn: ([dateString = '']) => dateString,
            }),
            createMinMaxDatePostprocessor({
                min,
                max,
                dateModeTemplate,
                dateSeparator,
            }),
        ],
    };
}

function maskitoParseDate(value, params) {
    const { mode, min, max } = withDateDefaults(params);
    const digitsPattern = mode.replaceAll(/[^dmy]/g, '');
    const digits = value.replaceAll(/\D+/g, '');
    if (digits.length !== digitsPattern.length) {
        return null;
    }
    const dateSegments = parseDateString(value, mode);
    const parsedDate = segmentsToDate(dateSegments);
    return mode.includes('y') ? clamp(parsedDate, min, max) : parsedDate;
}

const formatter = Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
});
function toDateSegments(date) {
    return formatter
        .formatToParts(date)
        .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});
}

function maskitoStringifyDate(date, params) {
    const { mode, separator, min, max } = withDateDefaults(params);
    const { year, ...segments } = toDateSegments(clamp(date, min, max));
    return toDateString({ ...segments, year: year.padStart(mode.match(/y/g)?.length ?? 0, '0') }, { dateMode: mode.replaceAll('/', separator) });
}

function createMinMaxRangeLengthPostprocessor({ dateModeTemplate, rangeSeparator, minLength, maxLength, max = DEFAULT_MAX_DATE, }) {
    return isEmpty(minLength) && isEmpty(maxLength)
        ? identity
        : ({ value, selection }) => {
            const dateStrings = parseDateRangeString(value, dateModeTemplate, rangeSeparator);
            if (dateStrings.length !== 2 ||
                dateStrings.some((date) => !isDateStringComplete(date, dateModeTemplate))) {
                return { value, selection };
            }
            const [fromDate, toDate] = dateStrings.map((dateString) => segmentsToDate(parseDateString(dateString, dateModeTemplate)));
            if (!fromDate || !toDate) {
                return { value, selection };
            }
            const minDistantToDate = appendDate(fromDate, minLength);
            const maxDistantToDate = isEmpty(maxLength)
                ? max
                : appendDate(fromDate, maxLength);
            const minLengthClampedToDate = clamp(toDate, minDistantToDate, max);
            const minMaxLengthClampedToDate = minLengthClampedToDate > maxDistantToDate
                ? maxDistantToDate
                : minLengthClampedToDate;
            return {
                selection,
                value: dateStrings[0] +
                    rangeSeparator +
                    toDateString(dateToSegments(minMaxLengthClampedToDate), {
                        dateMode: dateModeTemplate,
                    }),
            };
        };
}

function createSwapDatesPostprocessor({ dateModeTemplate, rangeSeparator, }) {
    return ({ value, selection }) => {
        const dateStrings = parseDateRangeString(value, dateModeTemplate, rangeSeparator);
        const isDateRangeComplete = dateStrings.length === 2 &&
            dateStrings.every((date) => isDateStringComplete(date, dateModeTemplate));
        const [from, to] = selection;
        const caretAtTheEnd = from >= value.length;
        const allValueSelected = from === 0 && to >= value.length; // dropping text inside with a pointer
        if (!(caretAtTheEnd || allValueSelected) || !isDateRangeComplete) {
            return { value, selection };
        }
        const [fromDate, toDate] = dateStrings.map((dateString) => segmentsToDate(parseDateString(dateString, dateModeTemplate)));
        return {
            selection,
            value: fromDate && toDate && fromDate > toDate
                ? dateStrings.reverse().join(rangeSeparator)
                : value,
        };
    };
}

function maskitoDateRange(params) {
    const { mode, separator: dateSeparator, min, max, } = withDateDefaults({
        ...params,
        separator: params.dateSeparator,
    });
    const rangeSeparator = params.rangeSeparator ??
        `${CHAR_NO_BREAK_SPACE}${CHAR_EN_DASH}${CHAR_NO_BREAK_SPACE}`;
    const dateModeTemplate = mode.split('/').join(dateSeparator);
    const dateMask = Array.from(dateModeTemplate).map((char) => dateSeparator.includes(char) ? char : /\d/);
    return {
        ...MASKITO_DEFAULT_OPTIONS,
        mask: [...dateMask, ...Array.from(rangeSeparator), ...dateMask],
        overwriteMode: 'replace',
        preprocessors: [
            createFullWidthToHalfWidthPreprocessor(),
            createFirstDateEndSeparatorPreprocessor({
                dateModeTemplate,
                dateSeparator,
                firstDateEndSeparator: rangeSeparator,
                pseudoFirstDateEndSeparators: POSSIBLE_DATE_RANGE_SEPARATOR,
            }),
            createZeroPlaceholdersPreprocessor(),
            normalizeDatePreprocessor({
                dateModeTemplate,
                rangeSeparator,
                dateSeparator,
            }),
            createValidDatePreprocessor({
                dateModeTemplate,
                rangeSeparator,
                dateSeparator,
            }),
        ],
        postprocessors: [
            createDateSegmentsZeroPaddingPostprocessor({
                dateModeTemplate,
                dateSeparator,
                splitFn: (value) => ({
                    dateStrings: parseDateRangeString(value, dateModeTemplate, rangeSeparator),
                }),
                uniteFn: (validatedDateStrings, initialValue) => validatedDateStrings.reduce((acc, dateString, dateIndex) => `${acc}${dateString}${!dateIndex && initialValue.includes(rangeSeparator)
                    ? rangeSeparator
                    : ''}`, ''),
            }),
            createMinMaxDatePostprocessor({
                min,
                max,
                dateModeTemplate,
                rangeSeparator,
                dateSeparator,
            }),
            createMinMaxRangeLengthPostprocessor({
                ...params,
                max,
                dateModeTemplate,
                rangeSeparator,
            }),
            createSwapDatesPostprocessor({
                dateModeTemplate,
                rangeSeparator,
            }),
        ],
    };
}

const AM = new Date(1970, 0, 1, 1, 45, 30, 123);
const PM = new Date(1970, 0, 1, 13, 45, 30, 123);
function getLocaleTimeParams(locale) {
    const formatter = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
    });
    const amParts = formatter.formatToParts(AM);
    const pmParts = formatter.formatToParts(PM);
    return {
        dayPeriod: [amParts, pmParts].map((x) => x.find((part) => part.type === 'dayPeriod')?.value ?? ''),
        separators: amParts
            .slice(amParts.findIndex((x) => x.type === 'hour'), amParts.findIndex((x) => x.type === 'fractionalSecond'))
            .filter((x) => x.type === 'literal')
            .map((x) => x.value),
    };
}

function withTimeDefaults({ mode, separators, timeSegmentMaxValues = {}, timeSegmentMinValues = {}, locale = '', ...params }) {
    const localeParams = locale ? getLocaleTimeParams(locale) : null;
    const dayPeriod = mode.includes('AA') // TODO(v6): drop backward-compat for `'... AA'` modes and require explicit `dayPeriod` for 12-hour format
        ? ['AM', 'PM']
        : (params.dayPeriod ?? localeParams?.dayPeriod ?? ['', '']);
    const hasMeridiem = hasDayPeriod(dayPeriod);
    const defaultSeparators = Array.from(mode.replace(' AA', '')).filter((char) => TIME_FIXED_CHARACTERS.includes(char));
    return {
        mode,
        locale,
        step: 0,
        prefix: '',
        postfix: '',
        ...params,
        dayPeriod,
        separators: defaultSeparators.map((fallback, i) => separators?.[i] ?? localeParams?.separators[i] ?? fallback),
        timeSegmentMinValues: {
            ...DEFAULT_TIME_SEGMENT_MIN_VALUES,
            ...(hasMeridiem ? { hours: 1 } : {}),
            ...timeSegmentMinValues,
        },
        timeSegmentMaxValues: {
            ...DEFAULT_TIME_SEGMENT_MAX_VALUES,
            ...(hasMeridiem ? { hours: 12 } : {}),
            ...timeSegmentMaxValues,
        },
    };
}

function isDateTimeStringComplete(dateTimeString, { dateMode, timeMode, dateTimeSeparator = DATE_TIME_SEPARATOR, }) {
    return (dateTimeString.length >=
        dateMode.length + timeMode.length + dateTimeSeparator.length &&
        (dateTimeString.split(dateTimeSeparator)[0] ?? '')
            .split(/\D/)
            .every((segment) => !/^0+$/.exec(segment)));
}

function maskitoTime(params) {
    const { mode, separators, dayPeriod, prefix, postfix, timeSegmentMinValues, timeSegmentMaxValues, step, } = withTimeDefaults(params);
    const maskExpression = [
        ...prefix,
        ...createTimeMaskExpression({ mode, separators, dayPeriod }),
    ];
    return {
        mask: postfix
            ? ({ value }) => cutExpression(maskExpression, value).concat(...postfix)
            : maskExpression,
        preprocessors: [
            createFullWidthToHalfWidthPreprocessor(),
            createColonConvertPreprocessor(),
            createZeroPlaceholdersPreprocessor(postfix),
            createMeridiemPreprocessor(dayPeriod),
            createInvalidTimeSegmentInsertionPreprocessor({
                mode,
                timeSegmentMinValues,
                timeSegmentMaxValues,
            }),
        ],
        postprocessors: [
            createMeridiemPostprocessor(dayPeriod),
            (elementState) => enrichTimeSegmentsWithZeroes(elementState, {
                mode,
                timeSegmentMaxValues,
                separators,
            }),
            maskitoPrefixPostprocessorGenerator(prefix),
            maskitoPostfixPostprocessorGenerator(postfix),
        ],
        plugins: [
            createTimeSegmentsSteppingPlugin({
                fullMode: mode,
                step,
                timeSegmentMinValues,
                timeSegmentMaxValues,
                dayPeriod,
            }),
            createMeridiemSteppingPlugin({
                dayPeriod,
                meridiemStartIndex: hasDayPeriod(dayPeriod)
                    ? maskExpression.length - dayPeriod[0].length
                    : -1,
            }),
        ],
        overwriteMode: 'replace',
    };
}
/**
 * Without cutting, the mask expression removes postfix on the last digit deletion
 * ___
 * Case 1 (static pattern mask expression)
 * Mask expression is [/\d/, /\d/, ':', /\d/, /\d/, ' left']
 * 12:34| left => Press Backspace => 12:3|
 * Mask correctly removes postfix because it's fixed characters after not yet inserted 4th digit.
 * ___
 * Case 2 (dynamic pattern mask expression)
 * Mask expression is [/\d/, /\d/, ':', /\d/, /\d/, ' left'] & textfield contains `12:34 left`
 * 12:34| left => Press Backspace => Mask expression becomes [/\d/, /\d/, ':', /\d/, ' left']  => 12:3| left
 * Mask correctly does not remove postfix because it's trailing fixed characters
 * and all non-fixed characters were already inserted.
 */
function cutExpression(expression, value) {
    let digitsCount = Math.min(value.replaceAll(/\D/g, '').length, expression.filter((x) => typeof x !== 'string').length) || 1;
    const afterLastDigit = expression.findIndex((x) => typeof x !== 'string' && !--digitsCount) + 1;
    return expression.slice(0, afterLastDigit);
}

/**
 * Converts a formatted time string to milliseconds based on the given `options.mode`.
 *
 * @param maskedTime a formatted time string by {@link maskitoTime} or {@link maskitoStringifyTime}
 * @param params
 */
function maskitoParseTime(maskedTime, params) {
    const { mode, dayPeriod, timeSegmentMaxValues } = withTimeDefaults(params);
    const msInSecond = timeSegmentMaxValues.milliseconds + 1;
    const msInMinute = (timeSegmentMaxValues.seconds + 1) * msInSecond;
    const msInHour = (timeSegmentMaxValues.minutes + 1) * msInMinute;
    const parsedTime = padStartTimeSegments(parseTimeString(maskedTime, mode));
    let hours = Number(parsedTime.hours ?? '');
    if (hasDayPeriod(dayPeriod) && Number.isFinite(hours)) {
        if (maskedTime.includes(dayPeriod[1])) {
            hours = hours < 12 ? hours + 12 : hours;
        }
        else {
            hours = hours === 12 ? 0 : hours;
        }
    }
    return (hours * msInHour +
        Number(parsedTime.minutes ?? '') * msInMinute +
        Number(parsedTime.seconds ?? '') * msInSecond +
        Number(parsedTime.milliseconds ?? ''));
}

/**
 * Converts milliseconds to a formatted time string based on the given `options.mode`.
 *
 * @param milliseconds unsigned integer milliseconds
 * @param params
 */
function maskitoStringifyTime(milliseconds, params) {
    const { mode, separators, dayPeriod, timeSegmentMaxValues } = withTimeDefaults(params);
    const hasMeridiem = hasDayPeriod(dayPeriod);
    const msInSecond = timeSegmentMaxValues.milliseconds + 1;
    const msInMinute = (timeSegmentMaxValues.seconds + 1) * msInSecond;
    const msInHour = (timeSegmentMaxValues.minutes + 1) * msInMinute;
    const hours = Math.trunc(milliseconds / msInHour);
    milliseconds -= hours * msInHour;
    const minutes = Math.trunc(milliseconds / msInMinute);
    milliseconds -= minutes * msInMinute;
    const seconds = Math.trunc(milliseconds / msInSecond);
    milliseconds -= seconds * msInSecond;
    const result = padStartTimeSegments({
        hours: hasMeridiem ? hours % 12 || 12 : hours,
        minutes,
        seconds,
        milliseconds,
    });
    const time = toTimeString(result, { mode, separators });
    return hasMeridiem
        ? `${time}${CHAR_NO_BREAK_SPACE}${dayPeriod[hours >= 12 ? 1 : 0]}`
        : time;
}

function maskitoParseDateTime(value, { locale, dateMode, timeMode = 'HH:MM', dateSeparator, dateTimeSeparator = DATE_TIME_SEPARATOR, ...params }) {
    const dateParams = withDateDefaults(locale
        ? { ...params, separator: dateSeparator, mode: dateMode, locale }
        : { ...params, separator: dateSeparator, mode: dateMode });
    const timeParams = withTimeDefaults({ ...params, locale, mode: timeMode });
    const [dateSegment = '', timeSegment = ''] = value.split(dateTimeSeparator);
    const digitsPattern = timeParams.mode.replaceAll(/[^HMS]/g, '');
    const digits = timeSegment.replaceAll(/\D+/g, '');
    if (digits.length !== digitsPattern.length) {
        return null;
    }
    const date = maskitoParseDate(dateSegment, {
        ...dateParams,
        // Skip bounds here — clamp the whole date + time once at the end instead
        min: undefined,
        max: undefined,
    });
    const time = maskitoParseTime(timeSegment, timeParams);
    if (!date) {
        return null;
    }
    const dateTime = new Date(Number(date) + time);
    return dateParams.mode.includes('y')
        ? clamp(dateTime, dateParams.min, dateParams.max)
        : dateTime;
}

const NON_DIGIT_PLACEHOLDER_RE = /[^dmy]/g;
const LEADING_NON_DIGIT_RE = /^\D*/;
function splitDateTimeString(dateTime, dateModeTemplate) {
    const dateDigitsCount = dateModeTemplate.replaceAll(NON_DIGIT_PLACEHOLDER_RE, '').length;
    const [date = ''] = new RegExp(String.raw `(\d[^\d]*){0,${dateDigitsCount - 1}}\d?`).exec(dateTime) ||
        [];
    const [dateTimeSeparator = ''] = LEADING_NON_DIGIT_RE.exec(dateTime.slice(date.length)) || [];
    return [date, dateTime.slice(date.length + dateTimeSeparator.length)];
}

function maskitoStringifyDateTime(date, { locale, dateMode, timeMode = 'HH:MM', dateSeparator, dateTimeSeparator = DATE_TIME_SEPARATOR, ...params }) {
    const dateParams = withDateDefaults(locale
        ? { ...params, locale, mode: dateMode, separator: dateSeparator }
        : { ...params, mode: dateMode, separator: dateSeparator });
    const timeParams = withTimeDefaults({ ...params, locale, mode: timeMode });
    const validatedDate = clamp(date, dateParams.min, dateParams.max);
    const dateString = maskitoStringifyDate(validatedDate, dateParams);
    const extractedTime = Number(validatedDate) -
        Number(new Date(validatedDate.getFullYear(), validatedDate.getMonth(), validatedDate.getDate()));
    const timeString = maskitoStringifyTime(extractedTime, timeParams);
    return `${dateString}${dateTimeSeparator}${timeString}`;
}

function createMinMaxDateTimePostprocessor({ dateModeTemplate, mode: timeMode, min = DEFAULT_MIN_DATE, max = DEFAULT_MAX_DATE, dateTimeSeparator, }) {
    return ({ value, selection }) => {
        const [dateString, timeString] = splitDateTimeString(value, dateModeTemplate);
        const parsedDate = parseDateString(dateString, dateModeTemplate);
        const parsedTime = parseTimeString(timeString, timeMode);
        if (!isDateTimeStringComplete(value, {
            dateMode: dateModeTemplate,
            timeMode,
            dateTimeSeparator,
        })) {
            const fixedDate = raiseSegmentValueToMin(parsedDate, dateModeTemplate);
            const { year, month, day } = isDateStringComplete(dateString, dateModeTemplate)
                ? dateToSegments(clamp(segmentsToDate(fixedDate), min, max))
                : fixedDate;
            const fixedValue = toDateString({
                year,
                month,
                day,
                ...parsedTime,
            }, { dateMode: dateModeTemplate, dateTimeSeparator, timeMode });
            const tail = value.slice(fixedValue.length);
            return {
                selection,
                value: `${fixedValue}${tail}`,
            };
        }
        const date = segmentsToDate(parsedDate, parsedTime);
        const clampedDate = clamp(date, min, max);
        // trailing segment separators or meridiem characters
        const [trailingNonDigitCharacters = ''] = value.match(/\D+$/g) || [];
        const validatedValue = `${toDateString(dateToSegments(clampedDate), {
            dateMode: dateModeTemplate,
            dateTimeSeparator,
            timeMode,
        })}${trailingNonDigitCharacters}`;
        return {
            selection,
            value: validatedValue,
        };
    };
}

function createValidDateTimePreprocessor({ dateModeTemplate, dateSeparator, dateTimeSeparator, mode: timeMode, timeSegmentMaxValues, separators, }) {
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        if (data === dateSeparator) {
            return {
                elementState,
                data: selection[0] === value.length ? data : '',
            };
        }
        const newCharacters = data.replaceAll(/\D/g, '');
        if (!newCharacters) {
            return { elementState, data };
        }
        const [from, rawTo] = skipNonDigitCharacters(value, selection);
        let to = rawTo + data.length;
        const newPossibleValue = `${value.slice(0, from)}${newCharacters}${value.slice(to)}`;
        const [dateString, timeString] = splitDateTimeString(newPossibleValue, dateModeTemplate);
        let validatedValue = '';
        const hasDateTimeSeparator = newPossibleValue.includes(dateTimeSeparator);
        const { validatedDateString, updatedSelection } = validateDateString({
            dateString,
            dateSeparator,
            dateModeTemplate,
            offset: 0,
            selection: [from, to],
        });
        if (dateString && !validatedDateString) {
            return { elementState, data: '' }; // prevent changes
        }
        to = updatedSelection[1];
        validatedValue += validatedDateString;
        const updatedTimeState = enrichTimeSegmentsWithZeroes({ value: timeString, selection: [from, to] }, { mode: timeMode, separators, timeSegmentMaxValues });
        to = updatedTimeState.selection[1];
        validatedValue += hasDateTimeSeparator
            ? `${dateTimeSeparator}${updatedTimeState.value}`
            : updatedTimeState.value;
        const newData = validatedValue.slice(from, to);
        return {
            elementState: {
                selection: [from, rawTo],
                value: `${validatedValue.slice(0, from)}${newData
                    .split(dateSeparator)
                    .map((segment) => '0'.repeat(segment.length))
                    .join(dateSeparator)}${validatedValue.slice(to)}`,
            },
            data: newData,
        };
    };
}

function maskitoDateTime({ locale, dateMode, timeMode = 'HH:MM', timeStep, dateTimeSeparator = DATE_TIME_SEPARATOR, ...params }) {
    const dateParams = withDateDefaults(locale
        ? { ...params, locale, mode: dateMode, separator: params.dateSeparator }
        : { ...params, mode: dateMode, separator: params.dateSeparator });
    const timeParams = withTimeDefaults({
        ...params,
        locale,
        mode: timeMode,
        step: timeStep,
    });
    const dateSeparator = dateParams.separator;
    const dateModeTemplate = dateParams.mode.split('/').join(dateSeparator);
    const fullMode = `${dateModeTemplate}${dateTimeSeparator}${timeParams.mode}`;
    const mask = [
        ...Array.from(dateModeTemplate).map((char) => dateSeparator.includes(char) ? char : /\d/),
        ...dateTimeSeparator.split(''),
        ...createTimeMaskExpression(timeParams),
    ];
    return {
        ...MASKITO_DEFAULT_OPTIONS,
        mask,
        overwriteMode: 'replace',
        preprocessors: [
            createFullWidthToHalfWidthPreprocessor(),
            createColonConvertPreprocessor(),
            createFirstDateEndSeparatorPreprocessor({
                dateModeTemplate,
                dateSeparator,
                firstDateEndSeparator: dateTimeSeparator,
                pseudoFirstDateEndSeparators: dateTimeSeparator.split(''),
            }),
            createZeroPlaceholdersPreprocessor(),
            createMeridiemPreprocessor(timeParams.dayPeriod),
            normalizeDatePreprocessor({
                dateModeTemplate,
                dateSeparator,
                dateTimeSeparator,
            }),
            createInvalidTimeSegmentInsertionPreprocessor({
                ...timeParams,
                parseValue: (x) => {
                    const [dateString, timeString] = splitDateTimeString(x, dateModeTemplate);
                    return { timeString, restValue: `${dateString}${dateTimeSeparator}` };
                },
            }),
            createValidDateTimePreprocessor({
                ...timeParams,
                dateModeTemplate,
                dateSeparator,
                dateTimeSeparator,
            }),
        ],
        postprocessors: [
            createMeridiemPostprocessor(timeParams.dayPeriod),
            createDateSegmentsZeroPaddingPostprocessor({
                dateModeTemplate,
                dateSeparator,
                splitFn: (value) => {
                    const [dateString, timeString] = splitDateTimeString(value, dateModeTemplate);
                    return { dateStrings: [dateString], restPart: timeString };
                },
                uniteFn: ([validatedDateString], initialValue) => validatedDateString +
                    (initialValue.includes(dateTimeSeparator) ? dateTimeSeparator : ''),
            }),
            createMinMaxDateTimePostprocessor({
                ...dateParams,
                ...timeParams,
                dateModeTemplate,
                dateTimeSeparator,
            }),
        ],
        plugins: [
            createTimeSegmentsSteppingPlugin({ ...timeParams, fullMode }),
            createMeridiemSteppingPlugin({
                ...timeParams,
                meridiemStartIndex: hasDayPeriod(timeParams.dayPeriod)
                    ? mask.length - timeParams.dayPeriod[0].length
                    : -1,
            }),
        ],
    };
}

const DEFAULT_THOUSAND_SEPARATOR_PATTERN = (digits) => digits.match(/\d{1,3}(?=(?:\d{3})*$)/g) ?? [];

function extractAffixes(value, { prefix, postfix, decimalSeparator, decimalPseudoSeparators, minusSign, minusPseudoSigns, maximumFractionDigits, }) {
    const decimalSeparators = [...decimalPseudoSeparators, decimalSeparator]
        .map((x) => `\\${x}`)
        .join('');
    const minuses = [...minusPseudoSigns, minusSign].map((x) => `\\${x}`).join('');
    const prefixRegExp = prefix &&
        new RegExp(`^([${minuses}])?(${prefix.split('').map(escapeRegExp).join('?')}?)`);
    const postfixRegExp = postfix && new RegExp(`${postfix.split('').map(escapeRegExp).join('?')}?$`);
    const [, , extractedPrefix = ''] = value.match(prefixRegExp) ?? [];
    const [extractedPostfix = ''] = value.match(postfixRegExp) ?? [];
    const cleanValue = value
        .replace(prefixRegExp, prefix && '$1')
        .replace(postfixRegExp, '');
    const leadingDecimalSeparatorRE = new RegExp(decimalSeparator && maximumFractionDigits > 0 ? `^[${decimalSeparators}]` : '');
    const leadingDigitsRE = new RegExp(value.endsWith(postfix) ? '' : String.raw `^\d+`);
    const trailingDecimalSeparatorRE = new RegExp(decimalSeparator && maximumFractionDigits > 0 ? `[${decimalSeparators}]$` : '');
    const trailingDigitsRE = new RegExp(value.startsWith(prefix) ? '' : String.raw `\d+$`);
    return {
        extractedPrefix: extractedPrefix
            .replace(trailingDecimalSeparatorRE, '')
            .replace(trailingDigitsRE, ''),
        extractedPostfix: extractedPostfix
            .replace(leadingDecimalSeparatorRE, '')
            .replace(leadingDigitsRE, ''),
        cleanValue: `${trailingDigitsRE.exec(extractedPrefix)?.[0] ?? ''}${trailingDecimalSeparatorRE.exec(extractedPrefix)?.[0] ?? ''}${cleanValue}${leadingDigitsRE.exec(extractedPostfix)?.[0] ?? ''}${leadingDecimalSeparatorRE.exec(extractedPostfix)?.[0] ?? ''}`,
    };
}

function generateMaskExpression({ decimalPseudoSeparators, decimalSeparator, maximumFractionDigits, min, minusSign, minusPseudoSigns, postfix, prefix, thousandSeparator, }) {
    const computedPrefix = min < 0 && [minusSign, ...minusPseudoSigns].includes(prefix)
        ? ''
        : computeAllOptionalCharsRegExp(prefix);
    const digit = String.raw `\d`;
    const optionalMinus = min < 0 ? `[${minusSign}${minusPseudoSigns.map((x) => `\\${x}`).join('')}]?` : '';
    const integerPart = thousandSeparator
        ? `[${digit}${escapeRegExp(thousandSeparator).replaceAll(/\s/g, String.raw `\s`)}]*`
        : `[${digit}]*`;
    const precisionPart = Number.isFinite(maximumFractionDigits)
        ? maximumFractionDigits
        : '';
    const decimalPart = maximumFractionDigits > 0
        ? `([${escapeRegExp(decimalSeparator)}${decimalPseudoSeparators
            .map(escapeRegExp)
            .join('')}]${digit}{0,${precisionPart}})?`
        : '';
    const computedPostfix = computeAllOptionalCharsRegExp(postfix);
    const beginning = `(${optionalMinus}${computedPrefix}|${computedPrefix}${optionalMinus})`;
    return new RegExp(`^${beginning}${integerPart}${decimalPart}${computedPostfix}$`);
}
function computeAllOptionalCharsRegExp(str) {
    return str
        ? str
            .split('')
            .map((char) => `${escapeRegExp(char)}?`)
            .join('')
        : '';
}

function getLocaleNumberParams(locale) {
    const separatorParts = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 1,
        useGrouping: true,
    }).formatToParts(1234.5);
    const thousandSeparator = separatorParts.find((part) => part.type === 'group')?.value ?? '';
    const decimalSeparator = separatorParts.find((part) => part.type === 'decimal')?.value ?? '.';
    if (!thousandSeparator) {
        return {
            thousandSeparator,
            decimalSeparator,
            thousandSeparatorPattern: DEFAULT_THOUSAND_SEPARATOR_PATTERN,
        };
    }
    const groupingFormatter = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
    return {
        thousandSeparator,
        decimalSeparator,
        thousandSeparatorPattern: (digits) => {
            if (!digits) {
                return [];
            }
            let pos = 0;
            return groupingFormatter
                .formatToParts(BigInt(`1${'0'.repeat(digits.length - 1)}`))
                .filter((part) => part.type === 'integer')
                .map((part) => {
                const group = digits.slice(pos, pos + part.value.length);
                pos += part.value.length;
                return group;
            });
        },
    };
}

function toNumberParts(value, params) {
    const { extractedPrefix, cleanValue, extractedPostfix } = extractAffixes(value, params);
    const { decimalSeparator, minusSign, minusPseudoSigns, decimalPseudoSeparators, maximumFractionDigits, } = params;
    const [integerWithMinus = '', decimalPart = ''] = decimalSeparator
        ? cleanValue.split(decimalSeparator)
        : [cleanValue];
    const minuses = [minusSign, ...minusPseudoSigns].map((x) => `\\${x}`).join('');
    const [, minus = '', integerPart = ''] = new RegExp(`^([${minuses}])?(.*)`).exec(integerWithMinus) || [];
    return {
        prefix: extractedPrefix,
        minus,
        integerPart,
        decimalPart,
        decimalSeparator: decimalSeparator && maximumFractionDigits > 0
            ? (new RegExp(`[${[decimalSeparator, ...decimalPseudoSeparators].map(escapeRegExp).join('')}]`, 'i').exec(cleanValue)?.[0] ?? '')
            : '',
        postfix: extractedPostfix,
    };
}
function fromNumberParts({ minus = '', integerPart = '', decimalPart = '', prefix = '', postfix = '', decimalSeparator = '', }, params) {
    const separator = decimalPart ? params.decimalSeparator : decimalSeparator;
    const beginning = params.negativePattern === 'minusFirst'
        ? `${minus}${prefix}`
        : `${prefix}${minus}`;
    return `${beginning}${integerPart}${separator}${decimalPart}${postfix}`;
}

function validateDecimalPseudoSeparators({ decimalSeparator, thousandSeparator, decimalPseudoSeparators = DEFAULT_DECIMAL_PSEUDO_SEPARATORS, }) {
    return decimalPseudoSeparators.filter((char) => char !== thousandSeparator && char !== decimalSeparator);
}

function withNumberDefaults({ locale = '', ...params } = {}) {
    const { max = Infinity, min = -Infinity, thousandSeparator = CHAR_NO_BREAK_SPACE, thousandSeparatorPattern = DEFAULT_THOUSAND_SEPARATOR_PATTERN, decimalSeparator = '.', decimalPseudoSeparators: unsafeDecimalPseudoSeparators, prefix = '', postfix = '', minusSign = CHAR_MINUS, minusPseudoSigns = DEFAULT_PSEUDO_MINUSES.filter((char) => char !== thousandSeparator &&
        char !== decimalSeparator &&
        char !== minusSign), maximumFractionDigits = 0, minimumFractionDigits = 0, negativePattern = 'prefixFirst', } = locale ? { ...getLocaleNumberParams(locale), ...params } : params;
    const decimalPseudoSeparators = validateDecimalPseudoSeparators({
        decimalSeparator,
        thousandSeparator,
        decimalPseudoSeparators: unsafeDecimalPseudoSeparators,
    });
    return {
        max,
        min,
        locale,
        thousandSeparator,
        thousandSeparatorPattern,
        postfix,
        minusSign,
        minusPseudoSigns,
        maximumFractionDigits,
        decimalPseudoSeparators,
        negativePattern,
        decimalSeparator: maximumFractionDigits <= 0 && decimalSeparator === thousandSeparator
            ? ''
            : decimalSeparator,
        prefix: prefix.endsWith(decimalSeparator) && maximumFractionDigits > 0
            ? `${prefix}${CHAR_ZERO_WIDTH_SPACE}`
            : prefix,
        minimumFractionDigits: Math.min(minimumFractionDigits, maximumFractionDigits),
    };
}

function maskitoParseNumber(maskedNumber, { bigint = false, ...optionalParams } = {}) {
    const params = withNumberDefaults(optionalParams);
    const { minus, integerPart, decimalSeparator, ...numberParts } = toNumberParts(maskedNumber, params);
    const unmaskedNumber = fromNumberParts({
        ...numberParts,
        integerPart: integerPart.replaceAll(/\D/g, ''),
        decimalSeparator: decimalSeparator && '.',
        prefix: '',
        postfix: '',
        minus: '',
    }, { ...params, decimalSeparator: '.' });
    if (unmaskedNumber) {
        const sign = minus ? CHAR_HYPHEN : '';
        return bigint
            ? BigInt(`${sign}${unmaskedNumber}`)
            : Number(`${sign}${unmaskedNumber}`);
    }
    return bigint ? null : Number.NaN;
}

const LOCALE = [];
const DEFAULT = {
    minusSign: CHAR_HYPHEN,
    minusPseudoSigns: [],
    prefix: '',
    postfix: '',
    decimalSeparator: '.',
    decimalPseudoSeparators: [],
    maximumFractionDigits: Infinity,
};
/**
 * Converts a number to a decimal string without using exponential notation.
 *
 * - Numbers without exponent are returned as-is.
 * - Numbers with a positive exponent (`e+N`) are expanded using locale formatting.
 * - Numbers with a negative exponent (`e-N`) are expanded manually to avoid
 *   precision limits of `Number#toFixed` and locale rounding.
 *
 * The sign of the number and the sign of the exponent are handled independently.
 * This guarantees correct formatting for cases like `-1.23e-8`.
 *
 * @param value Number or bigint to convert.
 * @returns Full decimal string representation without exponent notation.
 *
 * @example
 * stringifyNumberWithoutExp(1e25)
 * // "10000000000000000000000000"
 *
 * @example
 * stringifyNumberWithoutExp(1.23e-8)
 * // "0.0000000123"
 *
 * @example
 * stringifyNumberWithoutExp(-1.23e-8)
 * // "-0.0000000123"
 */
function stringifyNumberWithoutExp(value) {
    const valueAsString = String(value);
    const [numberPart = '', exponent] = valueAsString.split('e');
    if (!exponent) {
        return valueAsString;
    }
    if (!exponent.startsWith(CHAR_HYPHEN)) {
        return value.toLocaleString(LOCALE, { useGrouping: false });
    }
    const { minus, integerPart, decimalPart } = toNumberParts(numberPart, DEFAULT);
    const digits = `${integerPart}${decimalPart}`;
    const shift = Math.abs(Number(exponent));
    const totalZeros = shift - integerPart.length;
    let result;
    if (totalZeros >= 0) {
        result = `0.${'0'.repeat(totalZeros)}${digits}`;
    }
    else {
        const index = integerPart.length - shift;
        result = `${digits.slice(0, index)}.${digits.slice(index)}`;
    }
    return `${minus}${result}`;
}

/**
 * It drops prefix and postfix from data
 * Needed for case, when prefix or postfix contain decimalSeparator, to ignore it in resulting number
 * @example User pastes '{prefix}123.45{postfix}' => 123.45
 */
function createAffixesFilterPreprocessor(params) {
    return ({ elementState, data }) => {
        const { value } = elementState;
        const { prefix, postfix, ...numberParts } = toNumberParts(data, params);
        return {
            elementState,
            data: fromNumberParts({
                ...numberParts,
                prefix: value.startsWith(prefix) ? '' : prefix,
                postfix: value.endsWith(postfix) ? '' : postfix,
            }, params),
        };
    };
}

/**
 * If `minimumFractionDigits` is `>0`, it pads decimal part with zeroes
 * (until number of digits after decimalSeparator is equal to the `minimumFractionDigits`).
 * @example 1,42 => (`minimumFractionDigits` is equal to 4) => 1,4200.
 */
function createDecimalZeroPaddingPostprocessor(params) {
    const { minimumFractionDigits } = params;
    return minimumFractionDigits
        ? ({ value, selection }) => {
            if (Number.isNaN(maskitoParseNumber(value, params))) {
                return { value, selection };
            }
            const { decimalPart, ...numberParts } = toNumberParts(value, params);
            return {
                value: fromNumberParts({
                    ...numberParts,
                    decimalPart: decimalPart.padEnd(minimumFractionDigits, '0'),
                }, params),
                selection,
            };
        }
        : identity;
}

/**
 * Make textfield empty if there is no integer part and all decimal digits are zeroes.
 * @example 0|,00 => Backspace => Empty.
 * @example -0|,00 => Backspace => -.
 * @example ,42| => Backspace x2 => ,|00 => Backspace => Empty
 */
function emptyPostprocessor(params) {
    return ({ value, selection }) => {
        const [caretIndex] = selection;
        const { prefix, minus, integerPart, decimalSeparator, decimalPart, postfix } = toNumberParts(value, params);
        const aloneDecimalSeparator = !integerPart && !decimalPart && decimalSeparator;
        return (!integerPart &&
            !Number(decimalPart) &&
            caretIndex === `${minus}${prefix}`.length) ||
            aloneDecimalSeparator
            ? {
                selection,
                value: fromNumberParts({ prefix, minus, postfix }, params),
            }
            : { value, selection };
    };
}

/**
 * This preprocessor works only once at initialization phase (when `new Maskito(...)` is executed).
 * This preprocessor helps to avoid conflicts during transition from one mask to another (for the same input).
 * For example, the developer changes postfix (or other mask's props) during run-time.
 * ```
 * let maskitoOptions = maskitoNumber({postfix: ' year'});
 * // [3 seconds later]
 * maskitoOptions = maskitoNumber({postfix: ' years'});
 * ```
 */
function createInitializationOnlyPreprocessor(params) {
    let isInitializationPhase = true;
    const cleanNumberMask = generateMaskExpression({
        ...params,
        prefix: '',
        postfix: '',
        thousandSeparator: '',
        maximumFractionDigits: Infinity,
        min: -Infinity,
    });
    return ({ elementState, data }) => {
        if (!isInitializationPhase) {
            return { elementState, data };
        }
        isInitializationPhase = false;
        const { value, selection } = elementState;
        const [from, to] = selection;
        const { prefix, postfix, ...numberParts } = toNumberParts(value, params);
        const onlyNumber = fromNumberParts(numberParts, params);
        const cleanState = maskitoTransform({
            selection: [
                Math.max(from - prefix.length, 0),
                Math.max(to - prefix.length, 0),
            ],
            value: onlyNumber,
        }, { mask: cleanNumberMask });
        return {
            elementState: {
                selection: selection.map((position, i) => {
                    const deleted = onlyNumber.slice(0, Math.max(position - prefix.length, 0))
                        .length -
                        cleanState.value.slice(0, cleanState.selection[i]).length;
                    return Math.max(position - deleted, 0);
                }),
                value: fromNumberParts({
                    ...toNumberParts(cleanState.value, params),
                    prefix: prefix && params.prefix,
                    postfix: postfix && params.postfix,
                }, params),
            },
            data,
        };
    };
}

/**
 * If minus sign is positioned before prefix,
 * any attempt to erase prefix deletes minus (without deletion of non-removable prefix)
 * @example -$|42 => Backspace => $|42
 */
function createLeadingMinusDeletionPreprocessor(params) {
    return ({ elementState }, inputType) => {
        const { value, selection } = elementState;
        const [from, to] = selection;
        const { prefix, minusSign, negativePattern } = params;
        const beginning = negativePattern === 'prefixFirst' ? prefix : `${minusSign}${prefix}`;
        const newValue = fromNumberParts({ ...toNumberParts(value, params), minus: '' }, params);
        const diff = value.length - newValue.length;
        return {
            elementState: inputType.includes('delete') &&
                value.includes(minusSign) &&
                from < beginning.length
                ? {
                    value: newValue,
                    selection: [
                        Math.max(from - diff, beginning.length - 1),
                        Math.max(to - diff, beginning.length - 1),
                    ],
                }
                : elementState,
        };
    };
}

/**
 * It removes repeated leading zeroes for integer part.
 * @example 0,|00005 => Backspace => |5
 * @example -0,|00005 => Backspace => -|5
 * @example User types "000000" => 0|
 * @example 0| => User types "5" => 5|
 */
function createLeadingZeroesValidationPostprocessor(params) {
    const { thousandSeparator } = params;
    const trimLeadingZeroes = (value) => {
        const escapedThousandSeparator = escapeRegExp(thousandSeparator);
        return value
            .replace(
        // all leading zeroes followed by another zero
        new RegExp(String.raw `^(\D+)?[0${escapedThousandSeparator}]+(?=0)`), '$1')
            .replace(
        // zero followed by not-zero digit
        new RegExp(String.raw `^(\D+)?[0${escapedThousandSeparator}]+(?=[1-9])`), '$1');
    };
    const countTrimmedZeroesBefore = (value, index) => {
        const valueBefore = value.slice(0, index);
        const followedByZero = value.slice(index).startsWith('0');
        return (valueBefore.length -
            trimLeadingZeroes(valueBefore).length +
            (followedByZero ? 1 : 0));
    };
    return ({ value, selection }) => {
        const [from, to] = selection;
        const { integerPart, ...numberParts } = toNumberParts(value, params);
        const zeroTrimmedIntegerPart = trimLeadingZeroes(integerPart);
        if (integerPart === zeroTrimmedIntegerPart) {
            return { value, selection };
        }
        const newFrom = from - countTrimmedZeroesBefore(value, from);
        const newTo = to - countTrimmedZeroesBefore(value, to);
        return {
            value: fromNumberParts({ ...numberParts, integerPart: zeroTrimmedIntegerPart }, params),
            selection: [Math.max(newFrom, 0), Math.max(newTo, 0)],
        };
    };
}

/**
 * This postprocessor is connected with {@link createMinMaxPlugin}:
 * both validate `min`/`max` bounds of entered value (but at the different point of time).
 */
function createMinMaxPostprocessor(params) {
    const { decimalSeparator, maximumFractionDigits, min, max, minusSign } = params;
    return ({ value, selection }) => {
        const parsedNumber = maskitoParseNumber(value, {
            ...params,
            bigint: !maximumFractionDigits && !value.includes(decimalSeparator),
        }) ?? Number.NaN;
        const limitedValue = 
        /**
         * We cannot limit lower bound if user enters positive number.
         * The same for upper bound and negative number.
         * ___
         * @example (min = 5)
         * Empty input => Without this condition user cannot type 42 (the first digit will be rejected)
         * ___
         * @example (max = -10)
         * Value is -10 => Without this condition user cannot delete 0 to enter another digit
         */
        parsedNumber > 0 ? clamp(parsedNumber, null, max) : clamp(parsedNumber, min);
        if (parsedNumber && limitedValue !== parsedNumber) {
            const newValue = `${limitedValue}`
                .replace('.', decimalSeparator)
                .replace(CHAR_HYPHEN, minusSign);
            return {
                value: newValue,
                selection: [newValue.length, newValue.length],
            };
        }
        return {
            value,
            selection,
        };
    };
}

/**
 * Manage caret-navigation when user "deletes" non-removable digits or separators
 * @example 1,|42 => Backspace => 1|,42 (only if `minimumFractionDigits` is `>0`)
 * @example 1|,42 => Delete => 1,|42 (only if `minimumFractionDigits` is `>0`)
 * @example 0,|00 => Delete => 0,0|0 (only if `minimumFractionDigits` is `>0`)
 * @example 1 |000 => Backspace => 1| 000 (always)
 */
function createNonRemovableCharsDeletionPreprocessor({ decimalSeparator, thousandSeparator, minimumFractionDigits, }) {
    return ({ elementState, data }, actionType) => {
        const { value, selection } = elementState;
        const [from, to] = selection;
        const selectedCharacters = value.slice(from, to);
        const nonRemovableSeparators = minimumFractionDigits
            ? [decimalSeparator, thousandSeparator]
            : [thousandSeparator];
        const areNonRemovableZeroesSelected = Boolean(minimumFractionDigits) &&
            from > value.indexOf(decimalSeparator) &&
            Boolean(selectedCharacters.match(/^0+$/g));
        return (actionType !== 'deleteBackward' && actionType !== 'deleteForward') ||
            (!nonRemovableSeparators.includes(selectedCharacters) &&
                !areNonRemovableZeroesSelected)
            ? {
                elementState,
                data,
            }
            : {
                elementState: {
                    value,
                    selection: actionType === 'deleteForward' ? [to, to] : [from, from],
                },
                data,
            };
    };
}

/**
 * It pads integer part with zero if user types decimal separator (for empty input).
 * @example Empty input => User types "," (decimal separator) => 0,|
 */
function createNotEmptyIntegerPartPreprocessor(params) {
    const { maximumFractionDigits, decimalSeparator } = params;
    const startWithDecimalSepRegExp = new RegExp(String.raw `^\D*${escapeRegExp(decimalSeparator)}`);
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { prefix, postfix, ...numberParts } = toNumberParts(value, params);
        const onlyNumber = fromNumberParts(numberParts, params);
        const [from, to] = selection;
        const cleanFrom = clamp(from - prefix.length, 0, onlyNumber.length);
        const cleanTo = clamp(to - prefix.length, 0, onlyNumber.length);
        if (maximumFractionDigits <= 0 ||
            onlyNumber.slice(0, cleanFrom).includes(decimalSeparator) ||
            onlyNumber.slice(cleanTo).includes(decimalSeparator) ||
            !data.match(startWithDecimalSepRegExp)) {
            return { elementState, data };
        }
        const digitsBeforeCursor = /\d+/.exec(onlyNumber.slice(0, cleanFrom));
        return {
            elementState,
            data: digitsBeforeCursor ? data : `0${data}`,
        };
    };
}

function createNumberPrefixPostprocessor({ prefix, minusSign, negativePattern, }) {
    return ({ value, selection }, initialElementState) => maskitoPrefixPostprocessorGenerator(value.includes(minusSign) && negativePattern === 'minusFirst'
        ? `${minusSign}${prefix}`
        : prefix)({
        value: negativePattern === 'minusFirst' &&
            value.startsWith(`${prefix}${minusSign}`) // $-100 => -$100
            ? value.replace(`${prefix}${minusSign}`, `${minusSign}${prefix}`)
            : value,
        selection,
    }, initialElementState);
}

/**
 * It replaces pseudo characters with valid one.
 * @example User types '.' (but separator is equal to comma) => dot is replaced with comma.
 * @example User types hyphen / en-dash / em-dash => it is replaced with minus.
 */
function createPseudoCharactersPreprocessor({ validCharacter, pseudoCharacters, ...params }) {
    const pseudoCharactersRegExp = new RegExp(`[${pseudoCharacters.join('')}]`, 'gi');
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        const { prefix, postfix, ...numberParts } = toNumberParts(value, params);
        const onlyNumber = fromNumberParts(numberParts, params).replace(pseudoCharactersRegExp, validCharacter);
        return {
            elementState: {
                selection,
                value: fromNumberParts({
                    ...toNumberParts(onlyNumber, params),
                    prefix,
                    postfix,
                }, params),
            },
            data: data.replace(pseudoCharactersRegExp, validCharacter),
        };
    };
}

/**
 * It rejects new typed decimal separator if it already exists in text field.
 * Behaviour is similar to native <input type="number"> (Chrome).
 * @example 1|23,45 => Press comma (decimal separator) => 1|23,45 (do nothing).
 */
function createRepeatedDecimalSeparatorPreprocessor(params) {
    const { decimalSeparator } = params;
    return decimalSeparator
        ? ({ elementState, data }) => {
            const { value, selection } = elementState;
            const [from, to] = selection;
            return {
                elementState,
                data: !toNumberParts(value, params).decimalSeparator ||
                    value.slice(from, to + 1).includes(decimalSeparator)
                    ? data
                    : data.replaceAll(new RegExp(escapeRegExp(decimalSeparator), 'gi'), ''),
            };
        }
        : identity;
}

const SPACE_REG = /\s/;
const SPACE_GLOBAL_REG = /\s/g;
/**
 * It adds symbol for separating thousands.
 * @example 1000000 => (thousandSeparator is equal to space) => 1 000 000.
 */
function createThousandSeparatorPostprocessor(params) {
    const { thousandSeparator, thousandSeparatorPattern } = params;
    if (!thousandSeparator) {
        return identity;
    }
    const isSeparatorWhitespace = SPACE_REG.test(thousandSeparator);
    const isSeparator = isSeparatorWhitespace
        ? (char) => SPACE_REG.test(char)
        : (char) => char === thousandSeparator;
    const stripSeparators = isSeparatorWhitespace
        ? (str) => str.replaceAll(SPACE_GLOBAL_REG, '')
        : (str) => str.replaceAll(thousandSeparator, '');
    return ({ value, selection }) => {
        const [initialFrom, initialTo] = selection;
        let [from, to] = selection;
        const { prefix, minus, integerPart, decimalSeparator, decimalPart, postfix } = toNumberParts(value, params);
        const rawLength = `${minus}${integerPart}${decimalSeparator ? `${decimalSeparator}${decimalPart}` : ''}`
            .length;
        const normalizedLength = fromNumberParts({ minus, integerPart, decimalSeparator, decimalPart }, params).length;
        const deletedChars = normalizedLength - rawLength;
        if (deletedChars > 0 && initialFrom && initialFrom <= deletedChars) {
            from -= deletedChars;
        }
        if (deletedChars > 0 && initialTo && initialTo <= deletedChars) {
            to -= deletedChars;
        }
        const integerStart = prefix.length + minus.length;
        const groups = thousandSeparatorPattern(stripSeparators(integerPart));
        const digitAt = [];
        let pos = 0;
        for (const [i, group] of groups.entries()) {
            if (i > 0) {
                pos += thousandSeparator.length;
            }
            for (let j = 0; j < group.length; j++) {
                digitAt.push(pos + j);
            }
            pos += group.length;
        }
        const formatted = groups.join(thousandSeparator);
        const mapCursor = (cursor) => {
            const offset = cursor - integerStart;
            if (offset <= 0) {
                return cursor;
            }
            if (offset >= integerPart.length) {
                return cursor + formatted.length - integerPart.length;
            }
            const digitCount = stripSeparators(integerPart.slice(0, offset)).length;
            const prevWasSep = isSeparator(integerPart.charAt(offset - 1));
            return prevWasSep
                ? integerStart + (digitAt[digitCount] ?? formatted.length)
                : integerStart + (digitAt[digitCount - 1] ?? -1) + 1;
        };
        return {
            value: fromNumberParts({
                prefix,
                minus,
                integerPart: formatted,
                decimalSeparator,
                decimalPart,
                postfix,
            }, params),
            selection: [mapCursor(from), mapCursor(to)],
        };
    };
}

/**
 * It drops decimal part if `maximumFractionDigits` is zero.
 * @example User pastes '123.45' (but `maximumFractionDigits` is zero) => 123
 */
function createZeroPrecisionPreprocessor(params) {
    const { maximumFractionDigits, decimalSeparator } = params;
    if (maximumFractionDigits > 0 ||
        !decimalSeparator // all separators should be treated only as thousand separators
    ) {
        return identity;
    }
    const decimalPartRegExp = new RegExp(`${escapeRegExp(decimalSeparator)}.*$`, 'g');
    return ({ elementState, data }) => {
        const { value, selection } = elementState;
        const { prefix, postfix, ...numberParts } = toNumberParts(value, params);
        const [from, to] = selection;
        const onlyNumber = fromNumberParts(numberParts, params).replace(decimalPartRegExp, '');
        const newValue = fromNumberParts({ ...toNumberParts(onlyNumber, params), prefix, postfix }, params);
        return {
            elementState: {
                selection: [
                    Math.min(from, newValue.length),
                    Math.min(to, newValue.length),
                ],
                value: newValue,
            },
            data: data.replace(decimalPartRegExp, ''),
        };
    };
}

const DUMMY_SELECTION = [0, 0];
/**
 * It removes repeated leading zeroes for integer part on blur-event.
 * @example 000000 => blur => 0
 * @example 00005 => blur => 5
 */
function createLeadingZeroesValidationPlugin(params) {
    const dropRepeatedLeadingZeroes = createLeadingZeroesValidationPostprocessor(params);
    return maskitoEventHandler('blur', (element) => {
        const newValue = dropRepeatedLeadingZeroes({
            value: element.value,
            selection: DUMMY_SELECTION,
        }, { value: '', selection: DUMMY_SELECTION }).value;
        maskitoUpdateElement(element, newValue);
    }, { capture: true });
}

/**
 * This plugin is connected with {@link createMinMaxPostprocessor}:
 * both validate `min`/`max` bounds of entered value (but at the different point of time).
 */
function createMinMaxPlugin(params) {
    const { decimalSeparator, maximumFractionDigits, min, max } = params;
    return maskitoEventHandler('blur', (element, options) => {
        const parsedNumber = maskitoParseNumber(element.value, {
            ...params,
            bigint: !maximumFractionDigits &&
                !element.value.includes(decimalSeparator),
        }) ?? Number.NaN;
        const clampedNumber = clamp(parsedNumber, min, max);
        if (!Number.isNaN(parsedNumber) && parsedNumber !== clampedNumber) {
            maskitoUpdateElement(element, maskitoTransform(stringifyNumberWithoutExp(clampedNumber), options));
        }
    }, { capture: true });
}

/**
 * It pads EMPTY integer part with zero if decimal parts exists.
 * It works on blur event only!
 * @example 1|,23 => Backspace => Blur => 0,23
 */
function createNotEmptyIntegerPlugin(params) {
    const { decimalSeparator } = params;
    return decimalSeparator
        ? maskitoEventHandler('blur', (element) => {
            const { prefix, postfix, ...numberParts } = toNumberParts(element.value, params);
            const onlyNumber = fromNumberParts(numberParts, params).replace(new RegExp(String.raw `^(\D+)?${escapeRegExp(decimalSeparator)}`), `$10${decimalSeparator}`);
            const newValue = fromNumberParts({
                ...toNumberParts(onlyNumber, params),
                prefix,
                postfix,
            }, params);
            maskitoUpdateElement(element, newValue);
        }, { capture: true })
        : noop;
}

function maskitoNumber(options) {
    const params = withNumberDefaults(options);
    return {
        mask: generateMaskExpression(params),
        preprocessors: [
            createFullWidthToHalfWidthPreprocessor(),
            createInitializationOnlyPreprocessor(params),
            createAffixesFilterPreprocessor(params),
            createPseudoCharactersPreprocessor({
                ...params,
                validCharacter: params.minusSign,
                pseudoCharacters: params.minusPseudoSigns,
            }),
            createPseudoCharactersPreprocessor({
                ...params,
                validCharacter: params.decimalSeparator,
                pseudoCharacters: params.decimalPseudoSeparators,
            }),
            createNotEmptyIntegerPartPreprocessor(params),
            createNonRemovableCharsDeletionPreprocessor(params),
            createZeroPrecisionPreprocessor(params),
            createRepeatedDecimalSeparatorPreprocessor(params),
            createLeadingMinusDeletionPreprocessor(params),
        ],
        postprocessors: [
            createMinMaxPostprocessor(params),
            createNumberPrefixPostprocessor(params),
            maskitoPostfixPostprocessorGenerator(params.postfix),
            createThousandSeparatorPostprocessor(params),
            createDecimalZeroPaddingPostprocessor(params),
            emptyPostprocessor(params),
        ],
        plugins: [
            createLeadingZeroesValidationPlugin(params),
            createNotEmptyIntegerPlugin(params),
            createMinMaxPlugin(params),
        ],
        overwriteMode: params.minimumFractionDigits > 0
            ? ({ value, selection: [from] }) => from <= value.indexOf(params.decimalSeparator) ? 'shift' : 'replace'
            : 'shift',
    };
}

function maskitoStringifyNumber(number, optionalParams) {
    if (Number.isNaN(number) || number === null) {
        return '';
    }
    const params = withNumberDefaults(optionalParams);
    const value = stringifyNumberWithoutExp(clamp(number, params.min, params.max)).replace('.', params.decimalSeparator);
    return maskitoTransform(value, maskitoNumber(params));
}

export { maskitoAddOnFocusPlugin, maskitoCaretGuard, maskitoDate, maskitoDate as maskitoDateOptionsGenerator, maskitoDateRange, maskitoDateRange as maskitoDateRangeOptionsGenerator, maskitoDateTime, maskitoDateTime as maskitoDateTimeOptionsGenerator, maskitoEventHandler, maskitoNumber, maskitoNumber as maskitoNumberOptionsGenerator, maskitoParseDate, maskitoParseDateTime, maskitoParseNumber, maskitoParseTime, maskitoPostfixPostprocessorGenerator, maskitoPrefixPostprocessorGenerator, maskitoRejectEvent, maskitoRemoveOnBlurPlugin, maskitoSelectionChangeHandler, maskitoStringifyDate, maskitoStringifyDateTime, maskitoStringifyNumber, maskitoStringifyTime, maskitoTime, maskitoTime as maskitoTimeOptionsGenerator, maskitoWithPlaceholder };
